import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography, Paper, Divider, Tabs, Tab, IconButton, Tooltip, CircularProgress } from '@mui/material';
import Editor, { OnMount } from '@monaco-editor/react';
import Markdown from 'markdown-to-jsx';
// --- START: Added Imports for LogViewer ---
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { a11yDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
// --- END: Added Imports for LogViewer ---


// --- START: Interface and Prop Definitions ---

export interface Evidence {
  id: string;
  type: 'log' | 'metrics' | 'text' | 'code' | 'unknown';
  project: string;
  resource: string;
  timestamp: string;
  status: 'Failed to schedule' | 'Normal';
  details: string | object;
}

interface EvidencePanelProps {
  evidences: Evidence[];
  activeMessage: string | null;
}

type EvidenceTabType = 'log' | 'metrics' | 'text' | 'code';

// --- START: Utility Functions ---

const formatDate = (dateString: string) => {
  const options: Intl.DateTimeFormatOptions = {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  };
  return new Date(dateString).toLocaleString('en-US', options).replace(',', ' •');
};

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

const formatResourceName = (name: string) => {
  if (!name) return '';
  return name.split(/(?=[A-Z])/).join(' ');
};


// --- START: Upgraded Helper Components ---

const CodeViewer: React.FC<{ content: any; language?: string }> = ({ content, language = 'json' }) => {
    let stringContent = typeof content === 'string' ? content : JSON.stringify(content, null, 2);
    stringContent = stringContent.replace(/\\n/g, '\n').replace(/\\"/g, '"');


    const handleEditorDidMount: OnMount = (editor, monaco) => {
        editor.updateOptions({ readOnly: true, automaticLayout: true });
        monaco.editor.defineTheme('my-light-theme', {
            base: 'vs',
            inherit: true,
            rules: [],
            colors: {
                'editor.background': '#F9FAFB',
            },
        });
        monaco.editor.setTheme('my-light-theme');
    };
    
    return (
        <Box sx={{ height: '100%', minHeight: '400px', width: '100%'}}>
            <Editor
                height="400px"
                width="100%"
                language={language}
                value={stringContent}
                onMount={handleEditorDidMount}
                loading={<CircularProgress size={24} />}
                options={{
                    readOnly: true,
                    domReadOnly: true,
                    minimap: { enabled: false },
                    scrollBeyondLastLine: false,
                    contextmenu: false,
                    fontSize: 14,
                    wordWrap: 'on',
                }}
            />
        </Box>
    );
};

// --- NEW: LogViewer specifically using react-syntax-highlighter ---
const LogViewer: React.FC<{ content: string }> = ({ content }) => {
    const stringContent = content.replace(/\\n/g, '\n').replace(/\\"/g, '"').replace(/\\t/g, '\t');

    return (
        <SyntaxHighlighter
            language="log"
            style={a11yDark}
            wrapLines={true}
            customStyle={{
                margin: 0,
                padding: '12px',
                width: '100%',
                fontSize: '14px',
                backgroundColor: '#2b2b2b', // A dark background for the log viewer
                border: 'none',
                maxHeight: '500px'
            }}
             codeTagProps={{
                style: {
                    fontFamily: 'Roboto Mono, monospace',
                }
            }}
        >
            {stringContent}
        </SyntaxHighlighter>
    );
};


const MarkdownViewer: React.FC<{ content: string }> = ({ content }) => {
    return (
        <Box sx={{
            fontFamily: 'Inter, sans-serif', fontSize: '14px', color: '#333',
            p: 1.5, width: '100%', '& p': { lineHeight: 1.6 },
        }}>
            <Markdown
                options={{
                    overrides: {
                        pre: ({ children }) => {
                            if (React.isValidElement(children) && children.type === 'code') {
                                const props = children.props as { className?: string; children?: React.ReactNode };
                                const { className, children: codeContent } = props;
                                const language = className?.replace('lang-', '') || 'plaintext';
                                return <CodeViewer content={String(codeContent).trim()} language={language} />;
                            }
                            return <pre>{children}</pre>;
                        },
                        code: ({ children }) => <Box component="code" sx={{ 
                            bgcolor: 'rgba(0,0,0,0.06)', borderRadius: '4px',
                            p: '2px 5px', fontFamily: 'Roboto Mono, monospace', fontSize: '0.9em'
                        }}>{children}</Box>
                    },
                }}
            >
                {content}
            </Markdown>
        </Box>
    );
};

const formatMetricKey = (key: string) => {
    return key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
};

const isHtml = (str: string | undefined): str is string => {
    if (typeof str !== 'string') return false;
    const trimmedStr = str.trim();
    return trimmedStr.toLowerCase().startsWith('<!doctype html>') || trimmedStr.toLowerCase().startsWith('<html>');
};

interface Metric {
    aggregate?: number | string;
    timeseries?: string;
}

interface MetricsData {
    [key: string]: Metric;
}

const MetricsViewer: React.FC<{ data: MetricsData }> = ({ data }) => {
    return (
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2.5, py: 1, px: 0 }}>
            {Object.entries(data).map(([key, value]) => (
                <Paper key={key} variant="outlined" sx={{ width: '100%', borderRadius: '10px', overflow: 'hidden' }}>
                    <Box sx={{ p: 1.5, bgcolor: '#F8F9FA', borderBottom: '1px solid #EAEAEA' }}>
                        <Typography sx={{ fontWeight: 600, fontFamily: 'Inter, sans-serif', fontSize: '15px', color: '#343a40' }}>
                            {formatMetricKey(key)}
                        </Typography>
                    </Box>
                    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {value.aggregate !== undefined && (
                             <Box>
                                <Typography variant="caption" sx={{ color: '#6c757d', display: 'block', mb: 0.5, fontWeight: 500 }}>AGGREGATE</Typography>
                                <Typography sx={{ fontWeight: 500, fontFamily: 'Roboto Mono, monospace', color: '#03A65A', fontSize: '1.1rem' }}>{value.aggregate}</Typography>
                            </Box>
                        )}
                        {value.timeseries && (
                             <Box>
                                <Typography variant="caption" sx={{ color: '#6c757d', display: 'block', mb: 1, fontWeight: 500 }}>TIMESERIES</Typography>
                                {isHtml(value.timeseries) ? (
                                    <Box component="iframe" title={`Timeseries chart for ${formatMetricKey(key)}`} srcDoc={value.timeseries} sx={{ width: '100%', height: '300px', border: '1px solid #DEE2E6', borderRadius: '8px' }} sandbox="allow-scripts allow-same-origin"/>
                                ) : (
                                    <CodeViewer content={value.timeseries} language="json" />
                                )}
                            </Box>
                        )}
                    </Box>
                </Paper>
            ))}
        </Box>
    );
};

// --- START: Main Rendering Logic ---

const renderEvidenceContent = (evidence: Evidence) => {
    const getDetailsObject = (): object | null => {
        if (typeof evidence.details === 'object' && evidence.details !== null) return evidence.details;
        if (typeof evidence.details === 'string') {
            try { return JSON.parse(evidence.details); } catch (e) { return null; }
        }
        return null;
    };

    switch(evidence.type) {
        case 'metrics': {
            const detailsObject = getDetailsObject();
            if (detailsObject) {
                const metricsData = (detailsObject as any).data || detailsObject;
                if (typeof metricsData === 'object' && !Array.isArray(metricsData) && metricsData !== null) {
                    return <MetricsViewer data={metricsData} />;
                }
            }
            return <CodeViewer content={evidence.details} language="json" />;
        }
        case 'code':
             return <CodeViewer content={evidence.details} language="javascript" />;
        
        // --- UPDATED: Using the new LogViewer component ---
        case 'log': {
            let logContent = evidence.details;
            if (typeof logContent === 'object' && logContent !== null) {
                // Handle cases like {"pod_logs": "..."}
                const potentialKeys = ['pod_logs', 'log', 'logs', 'message', 'msg'];
                const logKey = Object.keys(logContent).find(k => potentialKeys.includes(k.toLowerCase()));
                if (logKey && typeof (logContent as any)[logKey] === 'string') {
                    logContent = (logContent as any)[logKey];
                } else {
                    // Fallback to stringifying the whole object if no known log key is found
                    logContent = JSON.stringify(logContent, null, 2);
                }
            }
            return <LogViewer content={logContent as string} />;
        }
        case 'text':{
            const detailsObject = getDetailsObject();
            if (detailsObject) {
                const summaryText = (detailsObject as any).summary || (detailsObject as any).response;
                if (summaryText && typeof summaryText === 'string') {
                    return <MarkdownViewer content={summaryText} />;
                }
                return <CodeViewer content={detailsObject} language="json" />;
            }
            return <MarkdownViewer content={evidence.details as string} />;
        }
        default:
            return <CodeViewer content={evidence.details} language="plaintext" />;
    }
}

// --- Main EvidencePanel Component (Unchanged) ---
const EvidencePanel: React.FC<EvidencePanelProps> = ({ evidences , activeMessage}) => {
  const [activeTab, setActiveTab] = useState<EvidenceTabType | ''>('');

  const availableTabs = useMemo(() => {
    const types = new Set(evidences.map(e => e.type));
    const tabOrder: EvidenceTabType[] = ['log', 'metrics', 'text', 'code'];
    return tabOrder.filter(tab => types.has(tab));
  }, [evidences]);

  useEffect(() => {
    if (availableTabs.length > 0 && (!activeTab || !availableTabs.includes(activeTab as EvidenceTabType))) {
      setActiveTab(availableTabs[0]);
    } else if (availableTabs.length === 0) {
      setActiveTab('');
    }
  }, [availableTabs, activeTab]);


  const handleTabChange = (event: React.SyntheticEvent, newValue: EvidenceTabType) => {
    setActiveTab(newValue);
  };
  
  const filteredEvidences = evidences.filter(e => e.type === activeTab);
  
  return (
    <Paper
      elevation={0}
      sx={{ width: '100%', bgcolor: '#FFFFFF', borderRadius: '12px', height: '100%', display: 'flex', flexDirection: 'column' }}
    >
      <Box sx={{ p: 2.5, pb: 0, flexShrink: 0 }}>
        <Typography sx={{ fontFamily:'Inter', fontSize:'18px', fontWeight: 600, color:'#333333' }}>Evidence Panel</Typography>
        <Tabs value={activeTab || false} onChange={handleTabChange} sx={{ minHeight: '40px', mt: 1}}>
          {availableTabs.map((tabType) => (
             <Tab key={tabType} label={capitalize(tabType)} value={tabType} sx={{ textTransform: 'none', minHeight: '40px', fontFamily:'Inter', fontSize:'14px', fontWeight: 400 }} />
          ))}
        </Tabs>
      </Box>
      <Divider />
      <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2.5, pt: 1 }}>
      {filteredEvidences.length === 0 ? (
        <Box sx={{display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center'}}>
           <Typography sx={{fontFamily:'Inter', fontSize:'14px', fontWeight: 400, color:'#333333'}}>
            {evidences.length > 0 ? 'Select a tab to view evidence.' : (activeMessage ? 'No evidence available for this message.':'Select a message to view its evidence')}
            </Typography>
        </Box>
      ) : (
        filteredEvidences.map((evidence) => (
          <Paper key={evidence.id} variant="outlined" sx={{ mb: 2 , borderRadius:'10px', borderColor:'1px solid #E6E6E6'}}>
            <Box sx={{ p: 1.5, bgcolor: '#F3F3F3', borderTopLeftRadius: '10px', borderTopRightRadius:'10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography sx={{ fontFamily:'Roboto Mono', fontSize:'12px', color:'#333333', fontWeight: 500 , lineHeight:'18px'}}>
                    {formatResourceName(evidence.resource).toUpperCase()}
                </Typography>
                <Tooltip title="Copy Content">
                    <IconButton size="small" onClick={() => {
                        const contentToCopy = typeof evidence.details === 'string' ? evidence.details : JSON.stringify(evidence.details, null, 2);
                        navigator.clipboard.writeText(contentToCopy)
                    }}>
                        <img src="/icons/copy-icon.svg" alt="Copy" style={{width: 16, height: 16}} />
                    </IconButton>
                </Tooltip>
            </Box>
            <Box sx={{ bgcolor: '#F9FAFB', borderBottomLeftRadius: '10px', borderBottomRightRadius:'10px', display: 'flex', width: '100%',overflowX: 'auto'}}>
             {renderEvidenceContent(evidence)}
            </Box>
          </Paper>
        ))
      )}
      </Box>
    </Paper>
  );
};

export default EvidencePanel;




// import React, { useState, useEffect, useMemo } from 'react';
// import { Box, Typography, Paper, Divider, Tabs, Tab, IconButton, Tooltip, CircularProgress } from '@mui/material';
// import Editor, { OnMount } from '@monaco-editor/react';
// import Markdown from 'markdown-to-jsx';

// // --- START: Interface and Prop Definitions ---

// export interface Evidence {
//   id: string;
//   type: 'log' | 'metrics' | 'text' | 'code' | 'unknown';
//   project: string;
//   resource: string;
//   timestamp: string;
//   status: 'Failed to schedule' | 'Normal';
//   details: string | object;
// }

// interface EvidencePanelProps {
//   evidences: Evidence[];
//   activeMessage: string | null;
// }

// type EvidenceTabType = 'log' | 'metrics' | 'text' | 'code';

// // --- START: Utility Functions ---

// const formatDate = (dateString: string) => {
//   const options: Intl.DateTimeFormatOptions = {
//     day: '2-digit',
//     month: 'short',
//     year: 'numeric',
//     hour: 'numeric',
//     minute: '2-digit',
//     hour12: true,
//   };
//   return new Date(dateString).toLocaleString('en-US', options).replace(',', ' •');
// };

// const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

// const formatResourceName = (name: string) => {
//   if (!name) return '';
//   return name.split(/(?=[A-Z])/).join(' ');
// };


// // --- START: Upgraded Helper Components ---

// // --- CodeViewer now used for Code, JSON, and Logs ---
// const CodeViewer: React.FC<{ content: any; language?: string }> = ({ content, language = 'json' }) => {
//     // Defensively handle content that might contain escaped newlines
//     let stringContent = typeof content === 'string' ? content : JSON.stringify(content, null, 2);
//     stringContent = stringContent.replace(/\\n/g, '\n').replace(/\\"/g, '"');


//     const handleEditorDidMount: OnMount = (editor, monaco) => {
//         editor.updateOptions({ readOnly: true, automaticLayout: true });
//         monaco.editor.defineTheme('my-light-theme', {
//             base: 'vs',
//             inherit: true,
//             rules: [],
//             colors: {
//                 'editor.background': '#F9FAFB',
//             },
//         });
//         monaco.editor.setTheme('my-light-theme');
//     };
    
//     return (
//         <Box sx={{ height: '100%', minHeight: '400px', width: '100%'}}>
//             <Editor
//                 height="400px"
//                 width="100%"
//                 language={language}
//                 value={stringContent}
//                 onMount={handleEditorDidMount}
//                 loading={<CircularProgress size={24} />}
//                 options={{
//                     readOnly: true,
//                     domReadOnly: true,
//                     minimap: { enabled: false },
//                     scrollBeyondLastLine: false,
//                     contextmenu: false,
//                     fontSize: 14,
//                     wordWrap: 'on',
//                 }}
//             />
//         </Box>
//     );
// };


// const MarkdownViewer: React.FC<{ content: string }> = ({ content }) => {
//     return (
//         <Box sx={{
//             fontFamily: 'Inter, sans-serif', fontSize: '14px', color: '#333',
//             p: 1.5, width: '100%', '& p': { lineHeight: 1.6 },
//         }}>
//             <Markdown
//                 options={{
//                     overrides: {
//                         pre: ({ children }) => {
//                             if (React.isValidElement(children) && children.type === 'code') {
//                                 const props = children.props as { className?: string; children?: React.ReactNode };
//                                 const { className, children: codeContent } = props;
//                                 const language = className?.replace('lang-', '') || 'plaintext';
//                                 return <CodeViewer content={String(codeContent).trim()} language={language} />;
//                             }
//                             return <pre>{children}</pre>;
//                         },
//                         code: ({ children }) => <Box component="code" sx={{ 
//                             bgcolor: 'rgba(0,0,0,0.06)', borderRadius: '4px',
//                             p: '2px 5px', fontFamily: 'Roboto Mono, monospace', fontSize: '0.9em'
//                         }}>{children}</Box>
//                     },
//                 }}
//             >
//                 {content}
//             </Markdown>
//         </Box>
//     );
// };

// const formatMetricKey = (key: string) => {
//     return key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
// };

// const isHtml = (str: string | undefined): str is string => {
//     if (typeof str !== 'string') return false;
//     const trimmedStr = str.trim();
//     return trimmedStr.toLowerCase().startsWith('<!doctype html>') || trimmedStr.toLowerCase().startsWith('<html>');
// };

// interface Metric {
//     aggregate?: number | string;
//     timeseries?: string;
// }

// interface MetricsData {
//     [key: string]: Metric;
// }

// const MetricsViewer: React.FC<{ data: MetricsData }> = ({ data }) => {
//     return (
//         <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2.5, py: 1, px: 0 }}>
//             {Object.entries(data).map(([key, value]) => (
//                 <Paper key={key} variant="outlined" sx={{ width: '100%', borderRadius: '10px', overflow: 'hidden' }}>
//                     <Box sx={{ p: 1.5, bgcolor: '#F8F9FA', borderBottom: '1px solid #EAEAEA' }}>
//                         <Typography sx={{ fontWeight: 600, fontFamily: 'Inter, sans-serif', fontSize: '15px', color: '#343a40' }}>
//                             {formatMetricKey(key)}
//                         </Typography>
//                     </Box>
//                     <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
//                         {value.aggregate !== undefined && (
//                              <Box>
//                                 <Typography variant="caption" sx={{ color: '#6c757d', display: 'block', mb: 0.5, fontWeight: 500 }}>AGGREGATE</Typography>
//                                 <Typography sx={{ fontWeight: 500, fontFamily: 'Roboto Mono, monospace', color: '#03A65A', fontSize: '1.1rem' }}>{value.aggregate}</Typography>
//                             </Box>
//                         )}
//                         {value.timeseries && (
//                              <Box>
//                                 <Typography variant="caption" sx={{ color: '#6c757d', display: 'block', mb: 1, fontWeight: 500 }}>TIMESERIES</Typography>
//                                 {isHtml(value.timeseries) ? (
//                                     <Box component="iframe" title={`Timeseries chart for ${formatMetricKey(key)}`} srcDoc={value.timeseries} sx={{ width: '100%', height: '300px', border: '1px solid #DEE2E6', borderRadius: '8px' }} sandbox="allow-scripts allow-same-origin"/>
//                                 ) : (
//                                     <CodeViewer content={value.timeseries} language="json" />
//                                 )}
//                             </Box>
//                         )}
//                     </Box>
//                 </Paper>
//             ))}
//         </Box>
//     );
// };

// // --- START: Main Rendering Logic ---

// const renderEvidenceContent = (evidence: Evidence) => {
//     const getDetailsObject = (): object | null => {
//         if (typeof evidence.details === 'object' && evidence.details !== null) return evidence.details;
//         if (typeof evidence.details === 'string') {
//             try { return JSON.parse(evidence.details); } catch (e) { return null; }
//         }
//         return null;
//     };

//     switch(evidence.type) {
//         case 'metrics': {
//             const detailsObject = getDetailsObject();
//             if (detailsObject) {
//                 const metricsData = (detailsObject as any).data || detailsObject;
//                 if (typeof metricsData === 'object' && !Array.isArray(metricsData) && metricsData !== null) {
//                     return <MetricsViewer data={metricsData} />;
//                 }
//             }
//             return <CodeViewer content={evidence.details} language="json" />;
//         }
//         case 'code':
//              return <CodeViewer content={evidence.details} language="javascript" />;
        
//         // --- FIXED: Log rendering is now smarter and uses CodeViewer ---
//         case 'log': {
//             let logContent = evidence.details;
//             if (typeof logContent === 'object' && logContent !== null) {
//                 // Handle cases like {"pod_logs": "..."}
//                 const potentialKeys = ['pod_logs', 'log', 'logs', 'message', 'msg'];
//                 const logKey = Object.keys(logContent).find(k => potentialKeys.includes(k.toLowerCase()));
//                 if (logKey && typeof (logContent as any)[logKey] === 'string') {
//                     logContent = (logContent as any)[logKey];
//                 } else {
//                     // Fallback to stringifying the whole object if no known log key is found
//                     logContent = JSON.stringify(logContent, null, 2);
//                 }
//             }
//             return <CodeViewer content={logContent} language="plaintext" />;
//         }
//         case 'text':{
//             const detailsObject = getDetailsObject();
//             if (detailsObject) {
//                 const summaryText = (detailsObject as any).summary || (detailsObject as any).response;
//                 if (summaryText && typeof summaryText === 'string') {
//                     return <MarkdownViewer content={summaryText} />;
//                 }
//                 return <CodeViewer content={detailsObject} language="json" />;
//             }
//             return <MarkdownViewer content={evidence.details as string} />;
//         }
//         default:
//             return <CodeViewer content={evidence.details} language="plaintext" />;
//     }
// }

// // --- Main EvidencePanel Component (Unchanged) ---
// const EvidencePanel: React.FC<EvidencePanelProps> = ({ evidences , activeMessage}) => {
//   const [activeTab, setActiveTab] = useState<EvidenceTabType | ''>('');

//   const availableTabs = useMemo(() => {
//     const types = new Set(evidences.map(e => e.type));
//     const tabOrder: EvidenceTabType[] = ['log', 'metrics', 'text', 'code'];
//     return tabOrder.filter(tab => types.has(tab));
//   }, [evidences]);

//   useEffect(() => {
//     if (availableTabs.length > 0 && (!activeTab || !availableTabs.includes(activeTab as EvidenceTabType))) {
//       setActiveTab(availableTabs[0]);
//     } else if (availableTabs.length === 0) {
//       setActiveTab('');
//     }
//   }, [availableTabs, activeTab]);


//   const handleTabChange = (event: React.SyntheticEvent, newValue: EvidenceTabType) => {
//     setActiveTab(newValue);
//   };
  
//   const filteredEvidences = evidences.filter(e => e.type === activeTab);
  
//   return (
//     <Paper
//       elevation={0}
//       sx={{ width: '100%', bgcolor: '#FFFFFF', borderRadius: '12px', height: '100%', display: 'flex', flexDirection: 'column' }}
//     >
//       <Box sx={{ p: 2.5, pb: 0, flexShrink: 0 }}>
//         <Typography sx={{ fontFamily:'Inter', fontSize:'18px', fontWeight: 600, color:'#333333' }}>Evidence Panel</Typography>
//         <Tabs value={activeTab || false} onChange={handleTabChange} sx={{ minHeight: '40px', mt: 1}}>
//           {availableTabs.map((tabType) => (
//              <Tab key={tabType} label={capitalize(tabType)} value={tabType} sx={{ textTransform: 'none', minHeight: '40px', fontFamily:'Inter', fontSize:'14px', fontWeight: 400 }} />
//           ))}
//         </Tabs>
//       </Box>
//       <Divider />
//       <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2.5, pt: 1 }}>
//       {filteredEvidences.length === 0 ? (
//         <Box sx={{display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center'}}>
//            <Typography sx={{fontFamily:'Inter', fontSize:'14px', fontWeight: 400, color:'#333333'}}>
//             {evidences.length > 0 ? 'Select a tab to view evidence.' : (activeMessage ? 'No evidence available for this message.':'Select a message to view its evidence')}
//             </Typography>
//         </Box>
//       ) : (
//         filteredEvidences.map((evidence) => (
//           <Paper key={evidence.id} variant="outlined" sx={{ mb: 2 , borderRadius:'10px', borderColor:'1px solid #E6E6E6'}}>
//             <Box sx={{ p: 1.5, bgcolor: '#F3F3F3', borderTopLeftRadius: '10px', borderTopRightRadius:'10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
//                 <Typography sx={{ fontFamily:'Roboto Mono', fontSize:'12px', color:'#333333', fontWeight: 500 , lineHeight:'18px'}}>
//                     {formatResourceName(evidence.resource).toUpperCase()}
//                 </Typography>
//                 <Tooltip title="Copy Content">
//                     <IconButton size="small" onClick={() => {
//                         const contentToCopy = typeof evidence.details === 'string' ? evidence.details : JSON.stringify(evidence.details, null, 2);
//                         navigator.clipboard.writeText(contentToCopy)
//                     }}>
//                         <img src="/icons/copy-icon.svg" alt="Copy" style={{width: 16, height: 16}} />
//                     </IconButton>
//                 </Tooltip>
//             </Box>
//             <Box sx={{ bgcolor: '#F9FAFB', borderBottomLeftRadius: '10px', borderBottomRightRadius:'10px', display: 'flex', width: '100%',}}>
//              {renderEvidenceContent(evidence)}
//             </Box>
//           </Paper>
//         ))
//       )}
//       </Box>
//     </Paper>
//   );
// };

// export default EvidencePanel;



// import React, { useState, useEffect, useMemo } from 'react';
// import { Box, Typography, Paper, Divider, Tabs, Tab, IconButton, Tooltip } from '@mui/material';
// import ReactMarkdown from 'react-markdown';
// // --- START: Added Imports for Enhanced Viewers ---
// import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
// import { materialLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
// import remarkGfm from 'remark-gfm'; // Plugin for tables, strikethrough, etc. in Markdown
// // --- END: Added Imports for Enhanced Viewers ---

// export interface Evidence {
//   id: string;
//   type: 'log' | 'metrics' | 'text' | 'code' | 'unknown';
//   project: string;
//   resource: string;
//   timestamp: string;
//   status: 'Failed to schedule' | 'Normal';
//   details: string | object;
// }

// interface EvidencePanelProps {
//   evidences: Evidence[];
//   activeMessage: string | null;
// }

// type EvidenceTabType = 'log' | 'metrics' | 'text' | 'code';

// const formatDate = (dateString: string) => {
//   const options: Intl.DateTimeFormatOptions = {
//     day: '2-digit',
//     month: 'short',
//     year: 'numeric',
//     hour: 'numeric',
//     minute: '2-digit',
//     hour12: true,
//   };
//   return new Date(dateString).toLocaleString('en-US', options).replace(',', ' •');
// };

// const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

// // --- START: Helper Components for Enhanced Rendering ---

// // --- NEW: Enhanced CodeViewer using react-syntax-highlighter ---
// const CodeViewer: React.FC<{ content: any; language?: string }> = ({ content, language = 'json' }) => {
//     // Ensure content is a string. If it's an object, stringify it with indentation.
//     const stringContent = typeof content === 'string' ? content : JSON.stringify(content, null, 2);

//     return (
//         <SyntaxHighlighter
//             language={language}
//             style={materialLight}
//             customStyle={{
//                 margin: 0,
//                 padding: '12px',
//                 width: '100%',
//                 backgroundColor: 'transparent',
//                 fontSize: '14px',
//                 wordBreak: 'break-all',
//                 whiteSpace: 'pre-wrap',
//             }}
//             codeTagProps={{
//                 style: {
//                     fontFamily: 'Roboto Mono, monospace',
//                 }
//             }}
//         >
//             {stringContent}
//         </SyntaxHighlighter>
//     );
// };

// // --- NEW: Enhanced LogViewer using react-syntax-highlighter for performance and accuracy ---
// const LogViewer: React.FC<{ content: string }> = ({ content }) => {
//     return (
//         <SyntaxHighlighter
//             language="log"
//             style={materialLight}
//             showLineNumbers={false}
//             wrapLines={true}
//             customStyle={{
//                 margin: 0,
//                 padding: '12px',
//                 width: '100%',
//                 backgroundColor: 'transparent',
//                 fontSize: '14px',
//                 border: 'none',
//             }}
//             codeTagProps={{
//                 style: {
//                     fontFamily: 'Roboto Mono, monospace',
//                     color: '#333'
//                 }
//             }}
//         >
//             {content.replace(/\\n/g, '\n')}
//         </SyntaxHighlighter>
//     );
// };


// // --- NEW: Enhanced MarkdownViewer with GFM (GitHub Flavored Markdown) support ---
// const MarkdownViewer: React.FC<{ content: string }> = ({ content }) => {
//     return (
//         <Box sx={{
//             fontFamily: 'Inter, sans-serif',
//             fontSize: '14px',
//             color: '#333',
//             wordBreak:'break-word',
//             px: 1.5,
//             py: 1.5,
//             width: '100%',
//             '& > :first-child': { marginTop: 0 },
//             '& > :last-child': { marginBottom: 0 },
//             '& p': { lineHeight: 1.6 },
//             '& a': { color: '#1976D2', textDecoration: 'underline' },
//             '& code': {
//                 backgroundColor: 'rgba(0,0,0,0.05)',
//                 borderRadius: '4px',
//                 padding: '2px 4px',
//                 fontFamily: 'Roboto Mono, monospace'
//             },
//             '& pre': {
//                 backgroundColor: 'rgba(0,0,0,0.05)',
//                 borderRadius: '4px',
//                 padding: '12px',
//                 overflowX: 'auto'
//             },
//             '& table': { borderCollapse: 'collapse', width: '100%' },
//             '& th, & td': { border: '1px solid #ddd', padding: '8px' },
//             '& th': { backgroundColor: '#f2f2f2' }
//         }}>
//             <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
//         </Box>
//     );
// };


// // --- START: Enhanced MetricsViewer Component ---

// const formatMetricKey = (key: string) => {
//     return key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
// };

// const isHtml = (str: string | undefined): str is string => {
//     if (typeof str !== 'string') {
//         return false;
//     }
//     const trimmedStr = str.trim();
//     return trimmedStr.toLowerCase().startsWith('<!doctype html>') || trimmedStr.toLowerCase().startsWith('<html>');
// };

// interface Metric {
//     aggregate?: number | string;
//     timeseries?: string;
// }

// interface MetricsData {
//     [key: string]: Metric;
// }

// const MetricsViewer: React.FC<{ data: MetricsData }> = ({ data }) => {
//     return (
//         <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2.5, py: 1, px: 0 }}>
//             {Object.entries(data).map(([key, value]) => {
//                 const timeseriesContent = value.timeseries;

//                 return (
//                     <Paper key={key} variant="outlined" sx={{ width: '100%', borderRadius: '10px', overflow: 'hidden' }}>
//                         <Box sx={{ p: 1.5, bgcolor: '#F8F9FA', borderBottom: '1px solid #EAEAEA' }}>
//                             <Typography sx={{ fontWeight: 600, fontFamily: 'Inter, sans-serif', fontSize: '15px', color: '#343a40' }}>
//                                 {formatMetricKey(key)}
//                             </Typography>
//                         </Box>

//                         <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
//                             {value.aggregate !== undefined && (
//                                 <Box>
//                                     <Typography variant="caption" sx={{ color: '#6c757d', display: 'block', mb: 0.5, fontWeight: 500 }}>
//                                         AGGREGATE
//                                     </Typography>
//                                     <Typography sx={{ fontWeight: 500, fontFamily: 'Roboto Mono, monospace', color: '#03A65A', fontSize: '1.1rem' }}>
//                                         {value.aggregate}
//                                     </Typography>
//                                 </Box>
//                             )}
                            
//                             {timeseriesContent && (
//                                  <Box>
//                                     <Typography variant="caption" sx={{ color: '#6c757d', display: 'block', mb: 1, fontWeight: 500 }}>
//                                         TIMESERIES
//                                     </Typography>
//                                     {isHtml(timeseriesContent) ? (
//                                         <Box
//                                             component="iframe"
//                                             title={`Timeseries chart for ${formatMetricKey(key)}`}
//                                             srcDoc={timeseriesContent}
//                                             sx={{
//                                                 width: '100%', height: '300px', border: '1px solid #DEE2E6',
//                                                 borderRadius: '8px',
//                                             }}
//                                             sandbox="allow-scripts allow-same-origin"
//                                         />
//                                     ) : (
//                                         // Fallback to CodeViewer for non-HTML timeseries
//                                         <CodeViewer content={timeseriesContent} language="text"/>
//                                     )}
//                                 </Box>
//                             )}
//                         </Box>
//                     </Paper>
//                 );
//             })}
//         </Box>
//     );
// };
// // --- END: Enhanced MetricsViewer Component ---


// const renderEvidenceContent = (evidence: Evidence) => {
//     const contentBoxStyle = {
//         maxHeight: '70vh',
//         overflowY: 'auto', display: 'flex', flexDirection: 'column',
//         width: '100%' , wordBreak:'break-all',
//     };

//     // Helper to safely get a parsed object from the details field
//     const getDetailsObject = (): object | null => {
//         if (typeof evidence.details === 'object' && evidence.details !== null) {
//             return evidence.details;
//         }
//         if (typeof evidence.details === 'string') {
//             try {
//                 return JSON.parse(evidence.details);
//             } catch (e) {
//                 return null;
//             }
//         }
//         return null;
//     };

//     switch(evidence.type) {
//         case 'metrics': {
//             const detailsObject = getDetailsObject();
//             if (detailsObject) {
//                 // Check if it has a 'data' key or if it's the data object itself
//                 const metricsData = (detailsObject as any).data || detailsObject;
//                 // Basic validation if metricsData is an object
//                 if (typeof metricsData === 'object' && !Array.isArray(metricsData) && metricsData !== null) {
//                     return (
//                         <Box sx={contentBoxStyle}>
//                             <MetricsViewer data={metricsData} />
//                         </Box>
//                     );
//                 }
//             }
//             // Fallback for non-JSON strings or invalid structure
//             return <Box sx={contentBoxStyle}><CodeViewer content={evidence.details} language="json" /></Box>;
//         }
//         case 'code': {
//             // Assume 'code' might be JSON or other languages, default to 'javascript' for wide compatibility
//              return <Box sx={contentBoxStyle}><CodeViewer content={evidence.details} language="javascript" /></Box>;
//         }
//         case 'log':
//             return <Box sx={contentBoxStyle}><LogViewer content={evidence.details as string} /></Box>;
//         case 'text':{
//             const detailsObject = getDetailsObject();
//             if (detailsObject) {
//                 // If details is a JSON object, look for a summary/response key to render as markdown
//                 const summaryText = (detailsObject as any).summary || (detailsObject as any).response;
//                 if (summaryText && typeof summaryText === 'string') {
//                     return <MarkdownViewer content={summaryText} />;
//                 }
//                 // Otherwise, render the whole object as code
//                 return <Box sx={contentBoxStyle}><CodeViewer content={detailsObject} language="json" /></Box>;
//             }
//             // If details is a plain string, render it as markdown
//             return <MarkdownViewer content={evidence.details as string} />;
//         }
//         default: {
//              // Fallback for 'unknown' or other types, render as text/code
//             return <Box sx={contentBoxStyle}><CodeViewer content={evidence.details} language="text" /></Box>;
//         }
//     }
// }

// const formatResourceName = (name: string) => {
//   if (!name) return '';
//   return name.split(/(?=[A-Z])/).join(' ');
// };

// const EvidencePanel: React.FC<EvidencePanelProps> = ({ evidences , activeMessage}) => {
//   const [activeTab, setActiveTab] = useState<EvidenceTabType | ''>('');

//   const availableTabs = useMemo(() => {
//     const types = new Set(evidences.map(e => e.type));
//     const tabOrder: EvidenceTabType[] = ['log', 'metrics', 'text', 'code'];
//     return tabOrder.filter(tab => types.has(tab));
//   }, [evidences]);

//   useEffect(() => {
//     if (availableTabs.length > 0 && (!activeTab || !availableTabs.includes(activeTab as EvidenceTabType))) {
//       setActiveTab(availableTabs[0]);
//     } else if (availableTabs.length === 0) {
//       setActiveTab('');
//     }
//   }, [availableTabs, activeTab]);


//   const handleTabChange = (event: React.SyntheticEvent, newValue: EvidenceTabType) => {
//     setActiveTab(newValue);
//   };
  
//   const filteredEvidences = evidences.filter(e => e.type === activeTab);
  
//   return (
//     <Paper
//       elevation={0}
//       sx={{
//         width: '100%',
//         bgcolor: '#FFFFFF',
//         borderRadius: '12px',
//         height: '100%',
//         display: 'flex',
//         flexDirection: 'column',
//       }}
//     >
//       <Box sx={{ p: 2.5, pb: 0, flexShrink: 0 }}>
//         <Typography sx={{ fontFamily:'Inter', fontSize:'18px', fontWeight: 600, color:'#333333' }}>
//           Evidence Panel
//         </Typography>
//         <Tabs value={activeTab || false} onChange={handleTabChange} sx={{ minHeight: '40px', mt: 1}}>
//           {availableTabs.map((tabType) => (
//              <Tab key={tabType} label={capitalize(tabType)} value={tabType} sx={{ textTransform: 'none', minHeight: '40px', fontFamily:'Inter', fontSize:'14px', fontWeight: 400 }} />
//           ))}
//         </Tabs>
//       </Box>
//       <Divider />
//       <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2.5, pt: 1 }}>
      
//       {filteredEvidences.length === 0 ? (
//         <Box sx={{display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center'}}>
//            <Typography sx={{fontFamily:'Inter', fontSize:'14px', fontWeight: 400, color:'#333333'}}>
//             {evidences.length > 0 ? 'Select a tab to view evidence.' : (activeMessage ? 'No evidence available for this message.':'Select a message to view its evidence')}
//             </Typography>
//         </Box>
//       ) : (
//         filteredEvidences.map((evidence) => (
//           <Paper key={evidence.id} variant="outlined" sx={{ mb: 2 , borderRadius:'10px', borderColor:'1px solid #E6E6E6'}}>
//             <Box sx={{ p: 1.5, bgcolor: '#F3F3F3', borderTopLeftRadius: '10px', borderTopRightRadius:'10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
//                 <Typography sx={{ fontFamily:'Roboto Mono', fontSize:'12px', color:'#333333', fontWeight: 500 , lineHeight:'18px'}}>
//                     {formatResourceName(evidence.resource).toUpperCase()}
//                 </Typography>
//                 <Tooltip title="Copy Content">
//                     <IconButton size="small" onClick={() => {
//                         const contentToCopy = typeof evidence.details === 'string' ? evidence.details : JSON.stringify(evidence.details, null, 2);
//                         navigator.clipboard.writeText(contentToCopy)
//                     }}>
//                         <img src="/icons/copy-icon.svg" alt="Copy" style={{width: 16, height: 16}} />
//                     </IconButton>
//                 </Tooltip>
//             </Box>
//             <Box sx={{p: 1, bgcolor: '#F9FAFB', borderBottomLeftRadius: '10px', borderBottomRightRadius:'10px', display: 'flex', width: '100%',}}>
//              {renderEvidenceContent(evidence)}
//             </Box>
//           </Paper>
//         ))
//       )}
//       </Box>
//     </Paper>
//   );
// };

// export default EvidencePanel;





// import React, { useState, useEffect, useMemo } from 'react';
// import { Box, Typography, Paper, Divider, Tabs, Tab, IconButton, Tooltip } from '@mui/material';
// import ReactMarkdown from 'react-markdown';

// export interface Evidence {
//   id: string;
//   type: 'log' | 'metrics' | 'text' | 'code' | 'unknown';
//   project: string;
//   resource: string;
//   timestamp: string;
//   status: 'Failed to schedule' | 'Normal';
//   details: string | object;
// }

// interface EvidencePanelProps {
//   evidences: Evidence[];
//   activeMessage: string | null;
// }

// type EvidenceTabType = 'log' | 'metrics' | 'text' | 'code';

// const formatDate = (dateString: string) => {
//   const options: Intl.DateTimeFormatOptions = {
//     day: '2-digit',
//     month: 'short',
//     year: 'numeric',
//     hour: 'numeric',
//     minute: '2-digit',
//     hour12: true,
//   };
//   return new Date(dateString).toLocaleString('en-US', options).replace(',', ' •');
// };

// const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

// // --- START: Helper Components for Enhanced Rendering ---

// const CodeViewer: React.FC<{ content: any }> = ({ content }) => {
//     // Defensively ensure content is a string. If it's an object, stringify it.
//     const stringContent = typeof content === 'string' ? content : JSON.stringify(content, null, 2);

//     return (
//         <Box component="div" sx={{ fontFamily: 'Roboto Mono', color: '#333', fontSize: '14px' , wordBreak:'break-all', whiteSpace: 'pre-wrap',  display: 'flex',
//         flexDirection: 'column',
//         width: '100%' ,px: 0.5,
//             py: 1.5,}}>
          
//             {stringContent.split('\n').map((line, i) => {
//                 const highlightedLine = line
//                     .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
//                     .replace(
//                         /([\w./-]+)(?=\s*:)/g, 
//                         '<span style="color: #03A65A;">$1</span>' // A slightly brighter green
//                     ); // Green keys
                    
//                 return <span key={i} style={{ display: 'block' }} dangerouslySetInnerHTML={{ __html: highlightedLine }} />;
//             })}
//         </Box>
//     );
// };

// const getLogLevelColor = (level: string): string => {
//     switch (level.toUpperCase()) {
//         case 'INFO':
//         case 'NOTICE':
//         case 'DEBUG':
//             return '#03A65A'; // Green
//         case 'WARN':
//         case 'WARNING':
//             return '#FFA600'; // Orange
//         case 'ERROR':
//         case 'FAIL':
//         case 'FATAL':
//         case 'CRITICAL':
//             return '#FF3023'; // Red
//         default:
//             return '#666666'; // Default Gray for others
//     }
// };

// const LogViewer: React.FC<{ content: string }> = ({ content }) => {
//     return (
//         <Box 
//             component="div" 
//             sx={{ 
//                 fontFamily: 'Roboto Mono, monospace', 
//                 color: '#333', 
//                 fontSize: '14px',
//                 whiteSpace: 'pre-wrap',
//                 wordBreak: 'break-all',
//                 width: '100%',
//                 px: 0.5,
//                 py: 1.5,
//             }}
//         >
//             {content.split('\\n').map((line, i) => {
//                 const sanitizedLine = line
//                     .replace(/&/g, '&amp;')
//                     .replace(/</g, '&lt;')
//                     .replace(/>/g, '&gt;');

//                 const highlightedLine = sanitizedLine.replace(
//                     /(\[([A-Z_]+)\])/gi,
//                     (fullMatch, tag, keyword) => {
//                         const color = getLogLevelColor(keyword);
//                         return `<span style="color: ${color}; font-weight: 600;">${tag}</span>`;
//                     }
//                 );
                    
//                 return (
//                     <span 
//                         key={i} 
//                         style={{ display: 'block', lineHeight: 1.7 }} 
//                         dangerouslySetInnerHTML={{ __html: highlightedLine }} 
//                     />
//                 );
//             })}
//         </Box>
//     );
// };

// const MarkdownViewer: React.FC<{ content: string }> = ({ content }) => {
//     return (
//         <Box sx={{ 
//             fontFamily: 'Inter, sans-serif', 
//             fontSize: '14px', 
//             color: '#333',
//             wordBreak:'break-word',
//             px: 0.5,
//             py: 1.5,
//             display: 'flex',
//             flexDirection: 'column',
//             width: '100%' ,
//             '& > :first-child': { marginTop: 0 },
//             '& > :last-child': { marginBottom: 0 },
//             '& p': { lineHeight: 1.6 },
//             '& a': { color: '#1976d2', textDecoration: 'underline' },
//             '& code': { 
//                 backgroundColor: 'rgba(0,0,0,0.05)', 
//                 borderRadius: '4px', 
//                 padding: '2px 4px', 
//                 fontFamily: 'Roboto Mono, monospace' 
//             },
//             '& pre': {
//                 backgroundColor: 'rgba(0,0,0,0.05)',
//                 borderRadius: '4px',
//                 padding: '12px',
//                 overflowX: 'auto'
//             }
//         }}>
//             <ReactMarkdown>{content}</ReactMarkdown>
//         </Box>
//     );
// };

// // --- START: Enhanced MetricsViewer Component ---

// const formatMetricKey = (key: string) => {
//     return key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
// };

// // This more robust function trims whitespace (like \n) before checking for HTML tags.
// const isHtml = (str: string | undefined): str is string => {
//     if (typeof str !== 'string') {
//         return false;
//     }
//     const trimmedStr = str.trim();
//     return trimmedStr.toLowerCase().startsWith('<!doctype html>') || trimmedStr.toLowerCase().startsWith('<html>');
// };

// interface Metric {
//     aggregate?: number | string;
//     timeseries?: string;
// }

// interface MetricsData {
//     [key: string]: Metric;
// }

// const MetricsViewer: React.FC<{ data: MetricsData }> = ({ data }) => {
//     return (
//         <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2.5, py: 1, px: 0 }}>
//             {Object.entries(data).map(([key, value]) => {
//                 // The timeseries string from a properly parsed JSON should not need manual cleaning.
//                 const timeseriesContent = value.timeseries;

//                 return (
//                     <Paper key={key} variant="outlined" sx={{ width: '100%', borderRadius: '10px', overflow: 'hidden' }}>
//                         <Box sx={{ p: 1.5, bgcolor: '#F8F9FA', borderBottom: '1px solid #EAEAEA' }}>
//                             <Typography sx={{ fontWeight: 600, fontFamily: 'Inter, sans-serif', fontSize: '15px', color: '#343a40' }}>
//                                 {formatMetricKey(key)}
//                             </Typography>
//                         </Box>

//                         <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
//                             {value.aggregate !== undefined && (
//                                 <Box>
//                                     <Typography variant="caption" sx={{ color: '#6c757d', display: 'block', mb: 0.5, fontWeight: 500 }}>
//                                         AGGREGATE
//                                     </Typography>
//                                     <Typography sx={{ fontWeight: 500, fontFamily: 'Roboto Mono, monospace', color: '#03A65A', fontSize: '1.1rem' }}>
//                                         {value.aggregate}
//                                     </Typography>
//                                 </Box>
//                             )}
                            
//                             {timeseriesContent && (
//                                  <Box>
//                                     <Typography variant="caption" sx={{ color: '#6c757d', display: 'block', mb: 1, fontWeight: 500 }}>
//                                         TIMESERIES
//                                     </Typography>
//                                     {isHtml(timeseriesContent) ? (
//                                         <Box 
//                                             component="iframe"
//                                             title={`Timeseries chart for ${formatMetricKey(key)}`}
//                                             srcDoc={timeseriesContent}
//                                             sx={{
//                                                 width: '100%', height: '300px', border: '1px solid #DEE2E6',
//                                                 borderRadius: '8px',
//                                             }}
//                                             sandbox="allow-scripts allow-same-origin"
//                                         />
//                                     ) : (
//                                         <CodeViewer content={timeseriesContent} />
//                                     )}
//                                 </Box>
//                             )}
//                         </Box>
//                     </Paper>
//                 );
//             })}
//         </Box>
//     );
// };
// // --- END: Enhanced MetricsViewer Component ---


// const renderEvidenceContent = (evidence: Evidence) => {
//     const contentBoxStyle = {
//         maxHeight: '70vh',
//         overflowY: 'auto', display: 'flex', flexDirection: 'column',
//         width: '100%' , wordBreak:'break-all',
//     };

//     // Helper to safely get a parsed object from the details field
//     const getDetailsObject = (): object | null => {
//         if (typeof evidence.details === 'object' && evidence.details !== null) {
//             return evidence.details; // It's already an object
//         }
//         if (typeof evidence.details === 'string') {
//             try {
//                 // Try to parse it as JSON
//                 return JSON.parse(evidence.details);
//             } catch (e) {
//                 // Not a valid JSON string, return null
//                 return null;
//             }
//         }
//         // Not a string or an object
//         return null;
//     };

//     switch(evidence.type) {
//         case 'metrics': {
//             const detailsObject = getDetailsObject();
//             if (detailsObject) {
//                 const metricsData = (detailsObject as any).data || detailsObject;
//                 return (
//                     <Box sx={contentBoxStyle}>
//                         <MetricsViewer data={metricsData} />
//                     </Box>
//                 );
//             }
//             return <Box sx={contentBoxStyle}><CodeViewer content={evidence.details} /></Box>;
//         }
//         case 'code': {
//             return <Box sx={contentBoxStyle}><CodeViewer content={evidence.details} /></Box>;
//         }
//         case 'log':
//             return <Box sx={contentBoxStyle}><LogViewer content={evidence.details as string} /></Box>;
//         case 'text':{
//             const detailsObject = getDetailsObject();
//             if (detailsObject) {
//                 const summaryText = (detailsObject as any).summary || (detailsObject as any).response;
//                 if (summaryText && typeof summaryText === 'string') {
//                     return <MarkdownViewer content={summaryText} />;
//                 }
//                 return <Box sx={contentBoxStyle}><CodeViewer content={detailsObject} /></Box>;
//             }
//             return <MarkdownViewer content={evidence.details as string} />;
//         }
//         default: {
//             return <Box sx={contentBoxStyle}><CodeViewer content={evidence.details} /></Box>;
//         }
//     }
// }

// const formatResourceName = (name: string) => {
//   if (!name) return '';
//   return name.split(/(?=[A-Z])/).join(' ');
// };

// const EvidencePanel: React.FC<EvidencePanelProps> = ({ evidences , activeMessage}) => {
//   const [activeTab, setActiveTab] = useState<EvidenceTabType | ''>('');

//   const availableTabs = useMemo(() => {
//     const types = new Set(evidences.map(e => e.type));
//     const tabOrder: EvidenceTabType[] = ['log', 'metrics', 'text', 'code'];
//     return tabOrder.filter(tab => types.has(tab));
//   }, [evidences]);

//   useEffect(() => {
//     if (availableTabs.length > 0 && (!activeTab || !availableTabs.includes(activeTab as EvidenceTabType))) {
//       setActiveTab(availableTabs[0]);
//     } else if (availableTabs.length === 0) {
//       setActiveTab('');
//     }
//   }, [availableTabs, activeTab]);


//   const handleTabChange = (event: React.SyntheticEvent, newValue: EvidenceTabType) => {
//     setActiveTab(newValue);
//   };
  
//   const filteredEvidences = evidences.filter(e => e.type === activeTab);
  
//   return (
//     <Paper
//       elevation={0}
//       sx={{
//         width: '100%',
//         bgcolor: '#FFFFFF',
//         borderRadius: '12px',
//         height: '100%',
//         display: 'flex',
//         flexDirection: 'column',
//       }}
//     >
//       <Box sx={{ p: 2.5, pb: 0, flexShrink: 0 }}>
//         <Typography sx={{ fontFamily:'Inter', fontSize:'18px', fontWeight: 600, color:'#333333' }}>
//           Evidence Panel
//         </Typography>
//         <Tabs value={activeTab || false} onChange={handleTabChange} sx={{ minHeight: '40px', mt: 1}}>
//           {availableTabs.map((tabType) => (
//              <Tab key={tabType} label={capitalize(tabType)} value={tabType} sx={{ textTransform: 'none', minHeight: '40px', fontFamily:'Inter', fontSize:'14px', fontWeight: 400 }} />
//           ))}
//         </Tabs>
//       </Box>
//       <Divider />
//       <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2.5, pt: 1 }}>
      
//       {filteredEvidences.length === 0 ? (
//         <Box sx={{display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center'}}>
//            <Typography sx={{fontFamily:'Inter', fontSize:'14px', fontWeight: 400, color:'#333333'}}>
//             {evidences.length > 0 ? 'Select a tab to view evidence.' : (activeMessage ? 'No evidence available for this message.':'Select a message to view its evidence')}
//             </Typography>
//         </Box>
//       ) : (
//         filteredEvidences.map((evidence) => (
//           <Paper key={evidence.id} variant="outlined" sx={{ mb: 2 , borderRadius:'10px', borderColor:'1px solid #E6E6E6'}}>
//             <Box sx={{ p: 1.5, bgcolor: '#F3F3F3', borderTopLeftRadius: '10px', borderTopRightRadius:'10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
//                 <Typography sx={{ fontFamily:'Roboto Mono', fontSize:'12px', color:'#333333', fontWeight: 500 , lineHeight:'18px'}}>
//                     {formatResourceName(evidence.resource).toUpperCase()}
//                 </Typography>
//                 <Tooltip title="Copy Content">
//                     <IconButton size="small" onClick={() => {
//                         const contentToCopy = typeof evidence.details === 'string' ? evidence.details : JSON.stringify(evidence.details, null, 2);
//                         navigator.clipboard.writeText(contentToCopy)
//                     }}>
//                         <img src="/icons/copy-icon.svg" alt="Copy" style={{width: 16, height: 16}} />
//                     </IconButton>
//                 </Tooltip>
//             </Box>
//             <Box sx={{p: 1, bgcolor: '#F9FAFB', borderBottomLeftRadius: '10px', borderBottomRightRadius:'10px', display: 'flex', width: '100%',}}>
//              {renderEvidenceContent(evidence)}
//             </Box>
//           </Paper>
//         ))
//       )}
//       </Box>
//     </Paper>
//   );
// };

// export default EvidencePanel;




// import React, { useState, useEffect, useMemo } from 'react';
// import { Box, Typography, Paper, Divider, Tabs, Tab, IconButton, Tooltip } from '@mui/material';
// import ReactMarkdown from 'react-markdown'; // <-- ADDED: New import for Markdown rendering

// export interface Evidence {
//   id: string;
//   type: 'log' | 'metrics' | 'text' | 'code' | 'unknown';
//   project: string;
//   resource: string;
//   timestamp: string;
//   status: 'Failed to schedule' | 'Normal';
//   details: string;
// }

// interface EvidencePanelProps {
//   evidences: Evidence[];
//   activeMessage: string | null;
// }

// type EvidenceTabType = 'log' | 'metrics' | 'text' | 'code';

// const formatDate = (dateString: string) => {
//   const options: Intl.DateTimeFormatOptions = {
//     day: '2-digit',
//     month: 'short',
//     year: 'numeric',
//     hour: 'numeric',
//     minute: '2-digit',
//     hour12: true,
//   };
//   return new Date(dateString).toLocaleString('en-US', options).replace(',', ' •');
// };

// const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

// // --- START: Helper Components for Enhanced Rendering ---

// const CodeViewer: React.FC<{ content: string }> = ({ content }) => {
//     return (
//         <Box component="div" sx={{ fontFamily: 'Roboto Mono', color: '#333', fontSize: '14px' , wordBreak:'break-all', whiteSpace: 'pre-wrap',  display: 'flex',
//         flexDirection: 'column',
//         width: '100%' ,px: 0.5,
//             py: 1.5,}}>
          
//             {content.split('\n').map((line, i) => {
//                 const highlightedLine = line
//                     .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
//                     .replace(
//                         /([\w./-]+)(?=\s*:)/g, 
//                         '<span style="color: #03A65A;">$1</span>' // A slightly brighter green
//                     ); // Green keys
                    
//                 return <span key={i} style={{ display: 'block' }} dangerouslySetInnerHTML={{ __html: highlightedLine }} />;
//             })}
//         </Box>
//     );
// };
// const getLogLevelColor = (level: string): string => {
//     switch (level.toUpperCase()) {
//         case 'INFO':
//         case 'NOTICE':
//         case 'DEBUG':
//             return '#03A65A'; // Green
//         case 'WARN':
//         case 'WARNING':
//             return '#FFA600'; // Orange
//         case 'ERROR':
//         case 'FAIL':
//         case 'FATAL':
//         case 'CRITICAL':
//             return '#FF3023'; // Red
//         default:
//             return '#666666'; // Default Gray for others
//     }
// };

// const LogViewer: React.FC<{ content: string }> = ({ content }) => {
//     return (
//         <Box 
//             component="div" 
//             sx={{ 
//                 fontFamily: 'Roboto Mono, monospace', 
//                 color: '#333', 
//                 fontSize: '14px',
//                 // These styles ensure indentation and long lines are handled correctly.
//                 whiteSpace: 'pre-wrap',
//                 wordBreak: 'break-all',
//                 width: '100%',
//                 px: 0.5,
//             py: 1.5,
//             }}
//         >
//             {/* Process each line to create a highlighted HTML string */}
//             {content.split('\\n').map((line, i) => {
//                 // Sanitize the line to prevent HTML injection, but keep brackets for the regex
//                 const sanitizedLine = line
//                     .replace(/&/g, '&amp;')
//                     .replace(/</g, '&lt;')
//                     .replace(/>/g, '&gt;');

//                 // Use a single replace with a function to handle all log levels
//                 const highlightedLine = sanitizedLine.replace(
//                     /(\[([A-Z_]+)\])/gi,
//                     (fullMatch, tag, keyword) => {
//                         const color = getLogLevelColor(keyword);
//                         return `<span style="color: ${color}; font-weight: 600;">${tag}</span>`;
//                     }
//                 );
                    
//                 return (
//                     <span 
//                         key={i} 
//                         style={{ display: 'block', lineHeight: 1.7 }} 
//                         dangerouslySetInnerHTML={{ __html: highlightedLine }} 
//                     />
//                 );
//             })}
//         </Box>
//     );
// };
// // const LogViewer: React.FC<{ content: string }> = ({ content }) => {
// //     return (
// //         <Box component="div" sx={{ fontFamily: 'Roboto Mono', color: '#333', fontSize: '14px',display: 'flex',
// //         flexDirection: 'column',
// //         width: '100%' , }}>
// //             {content.split('\\n').map((line, i) => {
// //                 const highlightedLine = line
// //                     .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br />')
// //                     .replace(/(\[NOTICE\]|\[INFO\])/gi, '<br /><span style="color: #03A65A; font-weight: 600;">$1</span>')
// //                     .replace(/(\[WARN\]|\[WARNING\])/gi, '<br /><span style="color: #ED6C02; font-weight: 600;">$1</span>')
// //                     .replace(/(\[ERROR\]|\[FAIL\])/gi, '<br /><span style="color: #D32F2F; font-weight: 600;">$1</span>')
                    
// //                 return <span key={i} style={{ display: 'block', lineHeight: 1.7 }} dangerouslySetInnerHTML={{ __html: highlightedLine }} />;
// //             })}
// //         </Box>
// //     );
// // };

// // --- START: MODIFIED SECTION ---
// // The previous MetricsViewer and TextViewer have been replaced with a more robust MarkdownViewer

// const MarkdownViewer: React.FC<{ content: string }> = ({ content }) => {
//     return (
//         <Box sx={{ 
//             fontFamily: 'Inter, sans-serif', 
//             fontSize: '14px', 
//             color: '#333',
//             wordBreak:'break-word',
//             px: 0.5,
//             py: 1.5,
//             display: 'flex',
//         flexDirection: 'column',
//         width: '100%' ,
//             '& > :first-child': { marginTop: 0 }, // Removes default margin from p, h1, etc.
//             '& > :last-child': { marginBottom: 0 },
//             '& p': { lineHeight: 1.6 },
//             '& a': { color: '#1976d2', textDecoration: 'underline' },
//             '& code': { 
//                 backgroundColor: 'rgba(0,0,0,0.05)', 
//                 borderRadius: '4px', 
//                 padding: '2px 4px', 
//                 fontFamily: 'Roboto Mono, monospace' 
//             },
//             '& pre': {
//                 backgroundColor: 'rgba(0,0,0,0.05)',
//                 borderRadius: '4px',
//                 padding: '12px',
//                 overflowX: 'auto'
//             }
//         }}>
//             <ReactMarkdown>{content}</ReactMarkdown>
//         </Box>
//     );
// };

// // --- END: MODIFIED SECTION ---


// const renderEvidenceContent = (evidence: Evidence) => {
//     const contentBoxStyle = {
//         maxHeight: '400px',
//         overflowY: 'auto',
//         display: 'flex',
//         flexDirection: 'column',
//         width: '100%' ,// Ensure box takes full width for scrollbar
//       wordBreak:'break-all',
//     };

//     // --- START: MODIFIED renderEvidenceContent function ---
//     switch(evidence.type) {
//         case 'metrics':
//         case 'code': {
//             let content = evidence.details;
//             try {
//                 // Pretty-print if it's a JSON string
//                 content = JSON.stringify(JSON.parse(evidence.details), null, 2);
//             } catch (e) { /* It wasn't JSON, use raw string (like YAML) */ }
//             return (
//                 <Box sx={contentBoxStyle}>
//                     <CodeViewer content={content} />
//                 </Box>
//             );
//         }
        
//         case 'log':
//             return (
//                 <Box sx={contentBoxStyle}>
//                     <LogViewer content={evidence.details} />
//                 </Box>
//             );

//         case 'text':{
//             let jsonObject;
//             try {
//                 // First, try to parse the details as JSON
//                 jsonObject = JSON.parse(evidence.details);
//             } catch (e) {
//                 // If it fails, it's not JSON. Treat as plain Markdown text.
//                 return <MarkdownViewer content={evidence.details} />;
//             }

//             // If it IS JSON, check for a summary/response key to render as Markdown
//             if (typeof jsonObject === 'object' && jsonObject !== null) {
//                 const summaryText = jsonObject.summary || jsonObject.response;
//                 if (summaryText && typeof summaryText === 'string') {
//                     return <MarkdownViewer content={summaryText} />;
//                 }
//             }

//             // If it's JSON but has no summary key, render the whole thing with CodeViewer
//             const prettyJson = JSON.stringify(jsonObject, null, 2);
//             return (
//                 <Box sx={contentBoxStyle}>
//                     <CodeViewer content={prettyJson} />
//                 </Box>
//             );
//         }
//         default: {
//             let jsonObject;
//             try {
//                 // First, try to parse the details as JSON
//                 jsonObject = JSON.parse(evidence.details);
//             } catch (e) {
//                 // If it fails, it's not JSON. Treat as plain Markdown text.
//                 return <MarkdownViewer content={evidence.details} />;
//             }

//             // If it IS JSON, check for a summary/response key to render as Markdown
//             if (typeof jsonObject === 'object' && jsonObject !== null) {
//                 const summaryText = jsonObject.summary || jsonObject.response;
//                 if (summaryText && typeof summaryText === 'string') {
//                     return <MarkdownViewer content={summaryText} />;
//                 }
//             }

//             // If it's JSON but has no summary key, render the whole thing with CodeViewer
//             const prettyJson = JSON.stringify(jsonObject, null, 2);
//             return (
//                 <Box sx={contentBoxStyle}>
//                     <CodeViewer content={prettyJson} />
//                 </Box>
//             );
//         }
//     }
//     // --- END: MODIFIED renderEvidenceContent function ---
// }

// const formatResourceName = (name: string) => {
//   if (!name) return '';
//   // This regex splits the string before each capital letter, then we join with a space.
//   return name.split(/(?=[A-Z])/).join(' ');
// };
// const EvidencePanel: React.FC<EvidencePanelProps> = ({ evidences , activeMessage}) => {
//   const [activeTab, setActiveTab] = useState<EvidenceTabType | ''>('');

//   const availableTabs = useMemo(() => {
//     const types = new Set(evidences.map(e => e.type));
//     const tabOrder: EvidenceTabType[] = ['log', 'metrics', 'text', 'code'];
//     return tabOrder.filter(tab => types.has(tab));
//   }, [evidences]);

//   useEffect(() => {
//     if (availableTabs.length > 0 && (!activeTab || !availableTabs.includes(activeTab as EvidenceTabType))) {
//       setActiveTab(availableTabs[0]);
//     } else if (availableTabs.length === 0) {
//       setActiveTab('');
//     }
//   }, [availableTabs, activeTab]);


//   const handleTabChange = (event: React.SyntheticEvent, newValue: EvidenceTabType) => {
//     setActiveTab(newValue);
//   };
  
//   const filteredEvidences = evidences.filter(e => e.type === activeTab);
  
//   return (
//     <Paper
//       elevation={0}
//       sx={{
//         width: '100%',
//         bgcolor: '#FFFFFF',
//         borderRadius: '12px',
//         height: '100%',
//         display: 'flex',
//         flexDirection: 'column',
//       }}
//     >
//       <Box sx={{ p: 2.5, pb: 0, flexShrink: 0 }}>
//         <Typography sx={{ fontFamily:'Inter', fontSize:'18px', fontWeight: 600, color:'#333333' }}>
//           Evidence Panel
//         </Typography>
//         <Tabs value={activeTab || false} onChange={handleTabChange} sx={{ minHeight: '40px', mt: 1}}>
//           {availableTabs.map((tabType) => (
//              <Tab key={tabType} label={capitalize(tabType)} value={tabType} sx={{ textTransform: 'none', minHeight: '40px', fontFamily:'Inter', fontSize:'14px', fontWeight: 400 }} />
//           ))}
//         </Tabs>
//       </Box>
//       <Divider />
//       <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2.5, pt: 1 }}>
      
//       {filteredEvidences.length === 0 ? (
//         <Box sx={{display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center'}}>
//            <Typography sx={{fontFamily:'Inter', fontSize:'14px', fontWeight: 400, color:'#333333'}}>
//             {evidences.length > 0 ? 'Select a tab to view evidence.' : (activeMessage ? 'No evidence available for this message.':'Select a message to view its evidence')}
//             </Typography>
//         </Box>
//       ) : (
//         filteredEvidences.map((evidence) => (
//           <Paper key={evidence.id} variant="outlined" sx={{ mb: 2 , borderRadius:'10px', borderColor:'1px solid #E6E6E6'}}>
//             <Box sx={{ p: 1.5, bgcolor: '#F3F3F3', borderTopLeftRadius: '10px', borderTopRightRadius:'10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
//                 <Typography sx={{ fontFamily:'Roboto Mono', fontSize:'12px', fontColor:'#333333', fontWeight: 500 , lineHeight:'18px'}}>
//                     {formatResourceName(evidence.resource).toUpperCase()}
//                 </Typography>
//                 <Tooltip title="Copy Content">
//                     <IconButton size="small" onClick={() => navigator.clipboard.writeText(evidence.details)}>
//                         <img src="/icons/copy-icon.svg" alt="Copy" style={{width: 16, height: 16}} />
//                     </IconButton>
//                 </Tooltip>
//             </Box>
//             {/* <Divider /> */}
//             <Box sx={{p: 1, bgcolor: '#F9FAFB', borderBottomLeftRadius: '10px', borderBottomRightRadius:'10px', display: 'flex', width: '100%',}}>
//              {renderEvidenceContent(evidence)}
//             </Box>
//           </Paper>
//         ))
//       )}
//       </Box>
//     </Paper>
//   );
// };

// export default EvidencePanel;



// import React, { useState, useEffect, useMemo } from 'react';
// import { Box, Typography, Paper, Divider, Tabs, Tab, IconButton, Tooltip } from '@mui/material';

// export interface Evidence {
//   id: string;
//   type: 'log' | 'metrics' | 'text' | 'code' | 'unknown';
//   project: string;
//   resource: string;
//   timestamp: string;
//   status: 'Failed to schedule' | 'Normal';
//   details: string;
// }

// interface EvidencePanelProps {
//   evidences: Evidence[];
// }

// type EvidenceTabType = 'log' | 'metrics' | 'text' | 'code';

// const formatDate = (dateString: string) => {
//   const options: Intl.DateTimeFormatOptions = {
//     day: '2-digit',
//     month: 'short',
//     year: 'numeric',
//     hour: 'numeric',
//     minute: '2-digit',
//     hour12: true,
//   };
//   return new Date(dateString).toLocaleString('en-US', options).replace(',', ' •');
// };

// const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

// // --- START: New Helper Components for Enhanced Rendering ---

// const CodeViewer: React.FC<{ content: string }> = ({ content }) => {
//     return (
//         <Box component="div" sx={{ fontFamily: 'Roboto Mono', color: '#333', fontSize: '14px' }}>
//             {content.split('\n').map((line, i) => {
//                 const highlightedLine = line
//                     .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
//                     .replace(
//                         /([\w./-]+)(?=\s*:)/g, 
//                         '<span style="color: #03A65A;">$1</span>' // A slightly brighter green
//                     ); // Green keys
                    
//                 return <span key={i} style={{ display: 'block' }} dangerouslySetInnerHTML={{ __html: highlightedLine }} />;
//             })}
//         </Box>
//     );
// };

// const LogViewer: React.FC<{ content: string }> = ({ content }) => {
//     return (
//         <Box component="div" sx={{ fontFamily: 'Roboto Mono', color: '#333', fontSize: '14px' }}>
//             {content.split('\\n').map((line, i) => {
//                 const highlightedLine = line
//                     .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br />')
//                     .replace(/(\[NOTICE\]|\[INFO\])/gi, '<br /><span style="color: #03A65A; font-weight: 600;">$1</span>')
//                     .replace(/(\[WARN\]|\[WARNING\])/gi, '<br /><span style="color: #ED6C02; font-weight: 600;">$1</span>')
//                     .replace(/(\[ERROR\]|\[FAIL\])/gi, '<br /><span style="color: #D32F2F; font-weight: 600;">$1</span>')
                    
//                 return <span key={i} style={{ display: 'block', lineHeight: 1.7 }} dangerouslySetInnerHTML={{ __html: highlightedLine }} />;
//             })}
//         </Box>
//     );
// };
// // --- START: ADDED MetricsViewer and TextViewer ---

// const MetricsViewer: React.FC<{ content: string }> = ({ content }) => {
//     return (
//         <Box component="div" sx={{ fontFamily: 'Roboto Mono', color: '#333', fontSize: '14px' }}>
//             {content.split('\n').map((line, i) => {
//                 const highlightedLine = line
//                     .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
//                     .replace(
//                         /([\w./-]+)(?=\s*:)/g, 
//                         '<span style="color: #03A65A;">$1</span>'
//                     );
                    
//                 return <span key={i} style={{ display: 'block' }} dangerouslySetInnerHTML={{ __html: highlightedLine }} />;
//             })}
//         </Box>
//     );
// };

// const TextViewer: React.FC<{ content: string }> = ({ content }) => {
//     return (
//         <Typography sx={{ 
//             p: 1.5, 
//             fontFamily: 'Roboto Mono', 
//             fontSize: '14px', 
//             color: '#333', 
//             whiteSpace: 'pre-line' // Preserves newlines from the string
//         }}>
//             {content}
//         </Typography>
//     );
// };

// // --- END: ADDED MetricsViewer and TextViewer ---
// const renderEvidenceContent = (evidence: Evidence) => {
//     const contentBoxStyle = {
//         p: 1.5,
//         maxHeight: '400px',
//         overflowY: 'auto'
//     };

//     switch(evidence.type) {
//        case 'metrics': {
//             let content = evidence.details;
//             try {
//                 content = JSON.stringify(JSON.parse(evidence.details), null, 2);
//             } catch (e) { /* It wasn't JSON, use raw string */ }
//             return (
//                 <Box sx={contentBoxStyle}>
//                     <MetricsViewer content={content} />
//                 </Box>
//             );
//         }
//         case 'code':
//             let content = evidence.details;
//             try {
//                 content = JSON.stringify(JSON.parse(evidence.details), null, 2);
//             } catch (e) { /* It wasn't JSON, use raw string (like YAML) */ }
//             return (
//                 <Box sx={contentBoxStyle}>
//                     <CodeViewer content={content} />
//                 </Box>
//             );
        
//         case 'log':
//             return (
//                 <Box sx={contentBoxStyle}>
//                     <LogViewer content={evidence.details} />
//                 </Box>
//             );

//         case 'text':{
//          let textContent = evidence.details;
//             try {
//                 const parsedData = JSON.parse(evidence.details);
//                 if (typeof parsedData === 'object' && parsedData !== null) {
//                     textContent = parsedData.summary || parsedData.response || JSON.stringify(parsedData, null, 2);
//                 }
//             } catch (e) { /* It's not JSON, use the raw string as is. */ }
            
//             // Use the new TextViewer for consistent component structure
//             return <TextViewer content={textContent} />;
//         }
        
//         default:{
//             let textContent = evidence.details;
//             try {
//                 const parsedData = JSON.parse(evidence.details);
//                 if (typeof parsedData === 'object' && parsedData !== null) {
//                     textContent = parsedData.summary || parsedData.response || JSON.stringify(parsedData, null, 2);
//                 }
//             } catch (e) { /* It's not JSON, use the raw string as is. */ }
            
//              return (
//                 <Typography sx={{ p: 1.5, fontFamily: 'Inter, sans-serif', fontSize: '14px', color: '#333', whiteSpace: 'pre-line' }}>
//                     {textContent}
//                 </Typography>
//             );
//     }}
// }
// // --- END: New Helper Components ---

// const EvidencePanel: React.FC<EvidencePanelProps> = ({ evidences }) => {
//   const [activeTab, setActiveTab] = useState<EvidenceTabType | ''>('');

//   const availableTabs = useMemo(() => {
//     const types = new Set(evidences.map(e => e.type));
//     const tabOrder: EvidenceTabType[] = ['log', 'metrics', 'text', 'code'];
//     return tabOrder.filter(tab => types.has(tab));
//   }, [evidences]);

//   useEffect(() => {
//     if (availableTabs.length > 0 && (!activeTab || !availableTabs.includes(activeTab as EvidenceTabType))) {
//       setActiveTab(availableTabs[0]);
//     } else if (availableTabs.length === 0) {
//       setActiveTab('');
//     }
//   }, [availableTabs, activeTab]);


//   const handleTabChange = (event: React.SyntheticEvent, newValue: EvidenceTabType) => {
//     setActiveTab(newValue);
//   };
  
//   const filteredEvidences = evidences.filter(e => e.type === activeTab);
  
//   return (
//     <Paper
//       elevation={0}
//       sx={{
//         maxWidth: '100%',
//         bgcolor: '#FFFFFF',
//         borderRadius: '12px',
//         height: '100%',
//         display: 'flex',
//         flexDirection: 'column',
//       }}
//     >
//       <Box sx={{ p: 2.5, pb: 0, flexShrink: 0 }}>
//         <Typography sx={{ fontFamily:'Inter', fontSize:'18px', fontWeight: 600, color:'#333333' }}>
//           Evidence
//         </Typography>
//         <Tabs value={activeTab || false} onChange={handleTabChange} sx={{ minHeight: '40px', mt: 1 }}>
//           {availableTabs.map((tabType) => (
//              <Tab key={tabType} label={capitalize(tabType)} value={tabType} sx={{ textTransform: 'none', minHeight: '40px' }} />
//           ))}
//         </Tabs>
//       </Box>
//       <Divider />
//       <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2.5, pt: 1 }}>
//       {filteredEvidences.length === 0 ? (
//         <Box sx={{display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center'}}>
//            <Typography color="text.secondary">
//             {evidences.length > 0 ? 'Select a tab to view evidence.' : 'No evidence available for this message.'}
//             </Typography>
//         </Box>
//       ) : (
//         filteredEvidences.map((evidence) => (
//           <Paper key={evidence.id} variant="outlined" sx={{ mb: 2 , borderRadius:'10px', borderColor:'1px solid #E6E6E6'}}>
//             <Box sx={{ p: 1.5, bgcolor: '#F3F3F3', borderTopLeftRadius: '10px', borderTopRightRadius:'10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
//                 <Typography sx={{ fontFamily:'Roboto Mono', fontSize:'12px', fontColor:'#333333', fontWeight: 500 , lineHeight:'18px'}}>
//                     {evidence.resource.toUpperCase()}
//                 </Typography>
//                 <Tooltip title="Copy Content">
//                     <IconButton size="small" onClick={() => navigator.clipboard.writeText(evidence.details)}>
//                         <img src="/icons/copy-icon.svg" alt="Copy" style={{width: 16, height: 16}} />
//                     </IconButton>
//                 </Tooltip>
//             </Box>
//             {/* <Divider /> */}
//             <Box sx={{p: 1, bgcolor: '#F9FAFB', borderBottomLeftRadius: '10px', borderBottomRightRadius:'10px', display: 'flex'}}>
//              {renderEvidenceContent(evidence)}
//             </Box>
//           </Paper>
//         ))
//       )}
//       </Box>
//     </Paper>
//   );
// };

// export default EvidencePanel;




// import React, { useState, useEffect, useMemo } from 'react';
// import { Box, Typography, Paper, Divider, Tabs, Tab, List, ListItem, ListItemText } from '@mui/material';

// export interface Evidence {
//   id: string;
//   type: 'log' | 'metrics' | 'text' | 'code' | 'unknown';
//   project: string;
//   resource: string;
//   timestamp: string;
//   status: 'Failed to schedule' | 'Normal';
//   details: string;
// }

// interface EvidencePanelProps {
//   evidences: Evidence[];
// }

// type EvidenceTabType = 'log' | 'metrics' | 'text' | 'code';

// const formatDate = (dateString: string) => {
//   const options: Intl.DateTimeFormatOptions = {
//     day: '2-digit',
//     month: 'short',
//     year: 'numeric',
//     hour: 'numeric',
//     minute: '2-digit',
//     hour12: true,
//   };
//   return new Date(dateString).toLocaleString('en-US', options).replace(',', ' •');
// };

// const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

// // Helper component to render individual evidence items based on their type
// const renderEvidenceContent = (evidence: Evidence) => {
//     const codeBlockStyle = {
//         fontFamily: 'monospace',
//         fontSize: '13px',
//         color: '#444',
//         bgcolor: '#f5f5f5',
//         border: '1px solid #ddd',
//         p: 1.5,
//         mt: 1,
//         borderRadius: '4px',
//         whiteSpace: 'pre-wrap',
//         wordBreak: 'break-all',
//         maxHeight: '400px',
//         overflowY: 'auto'
//     };

//     switch(evidence.type) {
//         case 'metrics':
//             try {
//                 const data = JSON.parse(evidence.details);
//                 return (
//                     <Box sx={{ p: 1.5, bgcolor: '#f5f5f5', borderRadius: '4px', border: '1px solid #ddd', mt: 1 }}>
//                         <List dense>
//                             {Object.entries(data).map(([key, value]) => (
//                                 <ListItem key={key} disableGutters>
//                                     <ListItemText 
//                                         primary={capitalize(key.replace(/_/g, ' '))}
//                                         secondary={typeof value === 'object' ? JSON.stringify(value) : String(value)} 
//                                         primaryTypographyProps={{ fontWeight: 'bold', color: 'text.primary' }}
//                                     />
//                                 </ListItem>
//                             ))}
//                         </List>
//                     </Box>
//                 );
//             } catch (e) {
//                 return <Typography sx={{ mt: 1, whiteSpace: 'pre-line', fontFamily: 'monospace' }}>{evidence.details}</Typography>;
//             }
        
//         case 'log':
//         case 'code':
//             let content = evidence.details;
//             try {
//                 content = JSON.stringify(JSON.parse(evidence.details), null, 2);
//             } catch (e) { /* It wasn't JSON, use raw string */ }
//             return (
//                 <Box component="pre" sx={codeBlockStyle}>
//                     {content}
//                 </Box>
//             );

//         case 'text':
//         default:
//             let textContent = evidence.details;
//             try {
//                 // If text type contains a JSON object, extract a human-readable summary
//                 const parsedData = JSON.parse(evidence.details);
//                 if (typeof parsedData === 'object' && parsedData !== null) {
//                     textContent = parsedData.summary || parsedData.response || evidence.details;
//                 }
//             } catch (e) { /* It's not JSON, so we'll just use the raw string as is. */ }
            
//              return (
//                 <Typography sx={{ 
//                     fontFamily: 'Inter, sans-serif', fontSize: '14px', color: '#333',
//                     p: 1.5, mt: 1, borderRadius: '4px', bgcolor: '#f9f9f9',
//                     whiteSpace: 'pre-line'
//                 }}>
//                     {textContent}
//                 </Typography>
//             );
//     }
// }

// const EvidencePanel: React.FC<EvidencePanelProps> = ({ evidences }) => {
//   const [activeTab, setActiveTab] = useState<EvidenceTabType | ''>('');

//   const availableTabs = useMemo(() => {
//     const types = new Set(evidences.map(e => e.type));
//     const tabOrder: EvidenceTabType[] = ['log', 'metrics', 'text', 'code'];
//     return tabOrder.filter(tab => types.has(tab));
//   }, [evidences]);

//   useEffect(() => {
//     if (availableTabs.length > 0 && !availableTabs.includes(activeTab as EvidenceTabType)) {
//       setActiveTab(availableTabs[0]);
//     } else if (availableTabs.length === 0) {
//       setActiveTab('');
//     }
//   }, [availableTabs, activeTab]);


//   const handleTabChange = (event: React.SyntheticEvent, newValue: EvidenceTabType) => {
//     setActiveTab(newValue);
//   };
  
//   const filteredEvidences = evidences.filter(e => e.type === activeTab);
  
//   return (
//     <Paper
//       elevation={0}
//       sx={{
//         width: '100%',
//         bgcolor: '#FFFFFF',
//         borderRadius: '12px',
//         height: '100%',
//         display: 'flex',
//         flexDirection: 'column',
//       }}
//     >
//       <Box sx={{ p: 2.5, pb: 0, flexShrink: 0 }}>
//         <Typography sx={{ fontFamily:'Inter', fontSize:'18px', fontWeight: 600, color:'#333333' }}>
//           Evidence Panel
//         </Typography>
//         <Tabs value={activeTab || false} onChange={handleTabChange} sx={{ minHeight: '40px', mt: 1 }}>
//           {availableTabs.map((tabType) => (
//              <Tab key={tabType} label={capitalize(tabType)} value={tabType} sx={{ textTransform: 'none', minHeight: '40px' }} />
//           ))}
//         </Tabs>
//       </Box>
//       <Divider />
//       <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2.5, pt: 0 }}>
//         {
//       filteredEvidences.length === 0 ? (
//         <Box sx={{display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center'}}>
//            <Typography sx={{ fontFamily:'Inter', fontSize:'14px', fontWeight: 400, color:'#333333' }}>
//             {evidences.length > 0 ? 'Select a tab to view evidence.' : 'No evidence available for this message.'}
//             </Typography>
//         </Box>
//       ) : (
//         filteredEvidences.map((evidence, index) => (
//           <React.Fragment key={evidence.id}>
//             <Box sx={{ py: 2.5 }}>
//               <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
//                 <Typography sx={{ fontFamily:'Inter', fontSize:'14px', color:'#333333', fontWeight: 'bold' }}>
//                     {evidence.resource}
//                 </Typography>
//                 <Typography sx={{ fontFamily:'Inter', fontSize:'12px', color:'#666666' }}>
//                   {formatDate(evidence.timestamp)}
//                 </Typography>
//               </Box>
//               {renderEvidenceContent(evidence)}
//             </Box>
//             {index < filteredEvidences.length - 1 && <Divider />}
//           </React.Fragment>
//         ))
//       )}
//       </Box>
//     </Paper>
//   );
// };

// export default EvidencePanel;





// import React, { useState, useEffect, useMemo } from 'react';
// import { Box, Typography, Paper, Divider, Tabs, Tab } from '@mui/material';

// // The Evidence type now includes a 'type' for categorization
// export interface Evidence {
//   id: string;
//   type: 'log' | 'metrics' | 'text' | 'code' | 'unknown';
//   project: string;
//   resource: string;
//   timestamp: string;
//   status: 'Failed to schedule' | 'Normal';
//   details: string;
// }

// interface EvidencePanelProps {
//   evidences: Evidence[];
// }

// // The types for the tabs that will be displayed in the UI
// type EvidenceTabType = 'log' | 'metrics' | 'text' | 'code';

// const formatDate = (dateString: string) => {
//   const options: Intl.DateTimeFormatOptions = {
//     day: '2-digit',
//     month: 'short',
//     year: 'numeric',
//     hour: 'numeric',
//     minute: '2-digit',
//     hour12: true,
//   };
//   return new Date(dateString).toLocaleString('en-US', options).replace(',', ' •');
// };

// const EvidencePanel: React.FC<EvidencePanelProps> = ({ evidences }) => {
//   const [activeTab, setActiveTab] = useState<EvidenceTabType | ''>('');

//   // Get a sorted, unique list of available evidence types for the current selection
//   const availableTabs = useMemo(() => {
//     const types = new Set(evidences.map(e => e.type));
//     const tabOrder: EvidenceTabType[] = ['log', 'metrics', 'text', 'code'];
//     // Filter the predefined order by what's available in the current evidence
//     return tabOrder.filter(tab => types.has(tab));
//   }, [evidences]);

//   // When the available tabs change, reset the active tab if needed
//   useEffect(() => {
//     if (availableTabs.length > 0 && !availableTabs.includes(activeTab as EvidenceTabType)) {
//       setActiveTab(availableTabs[0]);
//     } else if (availableTabs.length === 0) {
//       setActiveTab('');
//     }
//   }, [availableTabs, activeTab]);


//   const handleTabChange = (event: React.SyntheticEvent, newValue: EvidenceTabType) => {
//     setActiveTab(newValue);
//   };
  
//   const filteredEvidences = evidences.filter(e => e.type === activeTab);
  
//   const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

//   return (
//     <Paper
//       elevation={0}
//       sx={{
//         width: '100%',
//         bgcolor: '#FFFFFF',
//         borderRadius: '12px',
//         height: '100%',
//         display: 'flex',
//         flexDirection: 'column',
//       }}
//     >
//       <Box sx={{ p: 2.5, pb: 0, flexShrink: 0 }}>
//         <Typography sx={{ fontFamily:'Inter', fontSize:'18px', fontWeight: 600, color:'#333333' }}>
//           Evidence
//         </Typography>
//         <Tabs value={activeTab || false} onChange={handleTabChange} sx={{ minHeight: '40px', mt: 1 }}>
//           {availableTabs.map((tabType) => (
//              <Tab key={tabType} label={capitalize(tabType)} value={tabType} sx={{ textTransform: 'none', minHeight: '40px' }} />
//           ))}
//         </Tabs>
//       </Box>
//       <Divider />
//       <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2.5, pt: 0 }}>
//       {filteredEvidences.length === 0 ? (
//         <Box sx={{display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center'}}>
//            <Typography color="text.secondary">
//             {evidences.length > 0 ? 'Select a tab to view evidence.' : 'No evidence available for this message.'}
//             </Typography>
//         </Box>
//       ) : (
//         filteredEvidences.map((evidence, index) => (
//           <React.Fragment key={evidence.id}>
//             <Box sx={{ py: 2.5 }}>
//               <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
//                 <Typography sx={{ fontFamily:'Inter', fontSize:'12px', color:'#666666' }}>
//                   {evidence.project}
//                 </Typography>
//                 <Typography sx={{ fontFamily:'Inter', fontSize:'12px', color:'#666666' }}>
//                   {formatDate(evidence.timestamp)}
//                 </Typography>
//               </Box>
//               <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor:'#F9FAFB', p:1, borderRadius: '6px' }}>
//                   <Typography sx={{ fontFamily:'Inter', fontSize:'14px', color:'#333333' }}>
//                       {evidence.resource}
//                   </Typography>
//               </Box>
//               <Box
//                 component="pre"
//                 sx={{
//                   fontFamily: 'monospace',
//                   fontSize: '13px',
//                   color: '#444',
//                   bgcolor: '#f5f5f5',
//                   border: '1px solid #ddd',
//                   p: 1.5,
//                   mt: 1,
//                   borderRadius: '4px',
//                   whiteSpace: 'pre-wrap',
//                   wordBreak: 'break-all',
//                   maxHeight: '400px',
//                   overflowY: 'auto',
//                 }}
//               >
//                 {evidence.details}
//               </Box>
//             </Box>
//             {index < filteredEvidences.length - 1 && <Divider />}
//           </React.Fragment>
//         ))
//       )}
//       </Box>
//     </Paper>
//   );
// };

// export default EvidencePanel;




// import React, { useState, useEffect, useMemo } from 'react';
// import { Box, Typography, Paper, Divider, Tabs, Tab } from '@mui/material';

// // The Evidence type now includes a 'type' for categorization
// export interface Evidence {
//   id: string;
//   type: 'logs' | 'yaml' | 'metrics' | 'code' | 'unknown';
//   project: string;
//   resource: string;
//   timestamp: string;
//   status: 'Failed to schedule' | 'Normal';
//   details: string;
// }

// interface EvidencePanelProps {
//   evidences: Evidence[];
// }

// // Define the specific types a tab can be for reuse
// type EvidenceTabType = 'logs' | 'yaml' | 'metrics' | 'code';

// const formatDate = (dateString: string) => {
//   const options: Intl.DateTimeFormatOptions = {
//     day: '2-digit',
//     month: 'short',
//     year: 'numeric',
//     hour: 'numeric',
//     minute: '2-digit',
//     hour12: true,
//   };
//   return new Date(dateString).toLocaleString('en-US', options).replace(',', ' •');
// };

// const EvidencePanel: React.FC<EvidencePanelProps> = ({ evidences }) => {
//   // CORRECTED: The state now uses a specific union type, not a generic string.
//   const [activeTab, setActiveTab] = useState<EvidenceTabType | ''>('');

//   const availableTabs = useMemo(() => {
//     const types = new Set(evidences.map(e => e.type));
//     const tabOrder: EvidenceTabType[] = ['logs', 'yaml', 'metrics', 'code'];
//     // Filter the predefined order by what's available
//     return tabOrder.filter(tab => types.has(tab));
//   }, [evidences]);

//   useEffect(() => {
//     if (availableTabs.length > 0 && !availableTabs.includes(activeTab as EvidenceTabType)) {
//       setActiveTab(availableTabs[0]);
//     } else if (availableTabs.length === 0) {
//       setActiveTab('');
//     }
//   }, [availableTabs, activeTab]);


//   const handleTabChange = (event: React.SyntheticEvent, newValue: EvidenceTabType) => {
//     setActiveTab(newValue);
//   };
  
//   const filteredEvidences = evidences.filter(e => e.type === activeTab);
  
//   const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

//   return (
//     <Paper
//       elevation={0}
//       sx={{
//         width: '100%',
//         bgcolor: '#FFFFFF',
//         borderRadius: '12px',
//         height: '100%',
//         display: 'flex',
//         flexDirection: 'column',
//       }}
//     >
//       <Box sx={{ p: 2.5, pb: 0, flexShrink: 0 }}>
//         <Typography sx={{ fontFamily:'Inter', fontSize:'18px', fontWeight: 600, color:'#333333' }}>
//           Evidence
//         </Typography>
//         <Tabs value={activeTab} onChange={handleTabChange} sx={{ minHeight: '40px', mt: 1 }}>
//           {availableTabs.map((tabType) => (
//              <Tab key={tabType} label={capitalize(tabType)} value={tabType} sx={{ textTransform: 'none', minHeight: '40px' }} />
//           ))}
//         </Tabs>
//       </Box>
//       <Divider />
//       <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2.5, pt: 0 }}>
//       {filteredEvidences.length === 0 ? (
//         <Box sx={{display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center'}}>
//            <Typography color="text.secondary">
//             {evidences.length > 0 ? 'Select a tab to view evidence.' : 'No evidence available for this message.'}
//             </Typography>
//         </Box>
//       ) : (
//         filteredEvidences.map((evidence, index) => (
//           <React.Fragment key={evidence.id}>
//             <Box sx={{ py: 2.5 }}>
//               <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
//                 <Typography sx={{ fontFamily:'Inter', fontSize:'12px', color:'#666666' }}>
//                   {evidence.project}
//                 </Typography>
//                 <Typography sx={{ fontFamily:'Inter', fontSize:'12px', color:'#666666' }}>
//                   {formatDate(evidence.timestamp)}
//                 </Typography>
//               </Box>
//               <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor:'#F9FAFB', p:1, borderRadius: '6px' }}>
//                   <Typography sx={{ fontFamily:'Inter', fontSize:'14px', color:'#333333' }}>
//                       {evidence.resource}
//                   </Typography>
//               </Box>
//               <Box
//                 component="pre"
//                 sx={{
//                   fontFamily: 'monospace',
//                   fontSize: '13px',
//                   color: '#444',
//                   bgcolor: '#f5f5f5',
//                   border: '1px solid #ddd',
//                   p: 1.5,
//                   mt: 1,
//                   borderRadius: '4px',
//                   whiteSpace: 'pre-wrap',
//                   wordBreak: 'break-all',
//                   maxHeight: '400px',
//                   overflowY: 'auto',
//                 }}
//               >
//                 {evidence.details}
//               </Box>
//             </Box>
//             {index < filteredEvidences.length - 1 && <Divider />}
//           </React.Fragment>
//         ))
//       )}
//       </Box>
//     </Paper>
//   );
// };

// export default EvidencePanel;



// import React from 'react';
// import { Box, Typography, Paper, Chip, Divider } from '@mui/material';
// import { FiberManualRecord as FiberManualRecordIcon } from '@mui/icons-material';

// export interface Evidence {
//   id: string;
//   project: string;
//   resource: string;
//   timestamp: string;
//   status: 'Failed to schedule' | 'Normal';
//   details: string;
// }

// interface EvidencePanelProps {
//   evidences: Evidence[];
// }

// const formatDate = (dateString: string) => {
//   const options: Intl.DateTimeFormatOptions = {
//     day: '2-digit',
//     month: 'short',
//     year: 'numeric',
//     hour: 'numeric',
//     minute: '2-digit',
//     hour12: true,
//   };
//   return new Date(dateString).toLocaleString('en-US', options).replace(',', ' •');
// };

// const EvidencePanel: React.FC<EvidencePanelProps> = ({ evidences }) => {
//   return (
//     <Paper
//       elevation={0}
//       sx={{
//         width: '100%',
//         bgcolor: '#FFFFFF',
//         borderRadius: '12px',
//         p: 2.5,
//         height: '100%',
//         overflowY: 'auto',
//       }}
//     >
//       <Typography sx={{ fontFamily:'Inter', fontSize:'16px', fontWeight: 500, color:'#333333', mb: 2 }}>
//         Evidence Panel
//       </Typography>
//       <Divider sx={{ mx: -2.3 }} />
//       {evidences.map((evidence, index) => (
//         <React.Fragment key={evidence.id}>
//           <Box sx={{ py: 2.5 }}>
//             <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
//               <Typography sx={{ fontFamily:'Inter', fontSize:'12px', color:'#666666' }}>
//                 {evidence.project}
//               </Typography>
//               <Typography sx={{ fontFamily:'Inter', fontSize:'12px', color:'#666666' }}>
//                 {formatDate(evidence.timestamp)}
//               </Typography>
//             </Box>
            
//             <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor:'#F9FAFB', p:1, borderRadius: '6px' }}>
//                 <Typography sx={{ fontFamily:'Inter', fontSize:'14px', color:'#333333' }}>
//                     {evidence.resource}
//                 </Typography>
//                 {evidence.status === 'Failed to schedule' && (
//                 <Typography sx={{ fontFamily:'Inter', fontSize:'14px', color:'#CA2B20', display: 'flex', alignItems: 'center' }}>
//                   <Box sx={{ width: '8px', height: '8px', bgcolor: '#CA2B20', borderRadius: '50px', mr: 0.75, flexShrink: 0 }} /> 
//                   {evidence.status}
//                 </Typography>
//                 )

//                 }
//             </Box>

//             <Box
//               component="pre"
//               sx={{
//                 fontFamily: 'monospace',
//                 fontSize: '13px',
//                 color: '#444',
//                 bgcolor: '#f5f5f5',
//                 border: '1px solid #ddd',
//                 p: 1.5,
//                 mt: 1,
//                 borderRadius: '4px',
//                 whiteSpace: 'pre-wrap',
//                 wordBreak: 'break-all',
//                 maxHeight: '400px',
//                 overflowY: 'auto',
//               }}
//             >
//               {evidence.details}
//             </Box>

//           </Box>
//           {index < evidences.length - 1 && <Divider />}
//         </React.Fragment>
//       ))}
//     </Paper>
//   );
// };

// export default EvidencePanel;





// import React from 'react';
// import { Box, Typography, Paper, Chip, Divider } from '@mui/material';
// import { FiberManualRecord as FiberManualRecordIcon } from '@mui/icons-material';

// export interface Evidence {
//   id: string;
//   project: string;
//   resource: string;
//   timestamp: string;
//   status: 'Failed to schedule' | 'Normal';
//   details: string;
// }

// interface EvidencePanelProps {
//   evidences: Evidence[];
// }

// const formatDate = (dateString: string) => {
//   const options: Intl.DateTimeFormatOptions = {
//     day: '2-digit',
//     month: 'short',
//     year: 'numeric',
//     hour: 'numeric',
//     minute: '2-digit',
//     hour12: true,
//   };
//   return new Date(dateString).toLocaleString('en-US', options).replace(',', ' •');
// };

// const EvidencePanel: React.FC<EvidencePanelProps> = ({ evidences }) => {
//   return (
//     <Paper
//       elevation={0}
//       sx={{
//         width: '100%',
//         bgcolor: '#FFFFFF',
//         borderRadius: '12px',
//         p: 2.5,
//         height: '100%',
//         overflowY: 'auto',
//       }}
//     >
//       <Typography sx={{ fontFamily:'Inter', fontSize:'16px', fontWeight: 500, color:'#333333', mb: 2 }}>
//         Evidence Panel
//       </Typography>
//       <Divider sx={{ mx: -2.3 }} />
//       {evidences.map((evidence, index) => (
//         <React.Fragment key={evidence.id}>
//           <Box sx={{ py: 2.5 }}>
//             <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
//               <Typography sx={{ fontFamily:'Inter', fontSize:'12px', color:'#666666' }}>
//                 {evidence.project}
//               </Typography>
//               <Typography sx={{ fontFamily:'Inter', fontSize:'12px', color:'#666666' }}>
//                 {formatDate(evidence.timestamp)}
//               </Typography>
//             </Box>
            
//             <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor:'#F9FAFB', p:1, borderRadius: '6px' }}>
//                 <Typography sx={{ fontFamily:'Inter', fontSize:'14px', color:'#333333' }}>
//                     {evidence.resource}
//                 </Typography>
//                 {evidence.status === 'Failed to schedule' && (
//                 <Typography sx={{ fontFamily:'Inter', fontSize:'14px', color:'#CA2B20', display: 'flex', alignItems: 'center' }}>
//                   <Box sx={{ width: '8px', height: '8px', bgcolor: '#CA2B20', borderRadius: '50px', mr: 0.75, flexShrink: 0 }} /> 
//                   {evidence.status}
//                 </Typography>
//                 )

//                 }
//             </Box>
            
//             {/* CORRECTED: Replaced Typography with a styled Box to render a code block */}
//             <Box
//               component="pre"
//               sx={{
//                 fontFamily: 'monospace',
//                 fontSize: '13px',
//                 color: '#444',
//                 bgcolor: '#f5f5f5',
//                 border: '1px solid #ddd',
//                 p: 1.5,
//                 mt: 1,
//                 borderRadius: '4px',
//                 whiteSpace: 'pre-wrap',
//                 wordBreak: 'break-all',
//                 maxHeight: '400px',
//                 overflowY: 'auto',
//               }}
//             >
//               {evidence.details}
//             </Box>

//           </Box>
//           {index < evidences.length - 1 && <Divider />}
//         </React.Fragment>
//       ))}
//     </Paper>
//   );
// };

// export default EvidencePanel;



// import React from 'react';
// import { Box, Typography, Paper, Chip, Divider } from '@mui/material';
// import { FiberManualRecord as FiberManualRecordIcon } from '@mui/icons-material';

// export interface Evidence {
//   id: string;
//   project: string;
//   resource: string;
//   timestamp: string;
//   status: 'Failed to schedule' | 'Normal';
//   details: string;
// }

// interface EvidencePanelProps {
//   evidences: Evidence[];
// }

// const formatDate = (dateString: string) => {
//   const options: Intl.DateTimeFormatOptions = {
//     day: '2-digit',
//     month: 'short',
//     year: 'numeric',
//     hour: 'numeric',
//     minute: '2-digit',
//     hour12: true,
//   };
//   return new Date(dateString).toLocaleString('en-US', options).replace(',', ' •');
// };

// const EvidencePanel: React.FC<EvidencePanelProps> = ({ evidences }) => {
//   return (
//     <Paper
//       elevation={0}
//       sx={{
//         width: '100%',
//         bgcolor: '#FFFFFF',
//         borderRadius: '12px',
//         p: 2.5,
//         height: '100%',
//         overflowY: 'auto',
//         //border: '1px solid #EAECF0'
//       }}
//     >
//       <Typography sx={{ fontFamily:'Inter', fontSize:'16px', fontWeight: 500, color:'#333333', mb: 2 }}>
//         Evidence Panel
//       </Typography>
//       <Divider sx={{ mx: -2.3 }} />
//       {evidences.map((evidence, index) => (
//         <React.Fragment key={evidence.id}>
//           <Box sx={{ py: 2.5 }}>
//             <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
//               <Typography sx={{ fontFamily:'Inter', fontSize:'12px', color:'#666666' }}>
//                 {evidence.project}
//               </Typography>
//               <Typography sx={{ fontFamily:'Inter', fontSize:'12px', color:'#666666' }}>
//                 {formatDate(evidence.timestamp)}
//               </Typography>
//             </Box>
            
//             <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor:'#F9FAFB', p:1, borderRadius: '6px' }}>
//                 <Typography sx={{ fontFamily:'Inter', fontSize:'14px', color:'#333333' }}>
//                     {evidence.resource}
//                 </Typography>
//                 {evidence.status === 'Failed to schedule' && (
//                 <Typography sx={{ fontFamily:'Inter', fontSize:'14px', color:'#CA2B20', display: 'flex', alignItems: 'center' }}>
//                   <Box sx={{ width: '8px', height: '8px', bgcolor: '#CA2B20', borderRadius: '50px', mr: 0.75, flexShrink: 0 }} /> 
//                   {evidence.status}
//                 </Typography>
//                 )

//                 }
//             </Box>

//             <Typography sx={{ fontFamily:'Inter', fontSize:'14px', color:'#666666', mt: 1 }}>
//               {evidence.details}
//             </Typography>

//           </Box>
//           {index < evidences.length - 1 && <Divider />}
//         </React.Fragment>
//       ))}
//     </Paper>
//   );
// };

// export default EvidencePanel;




// import React from 'react';
// import { Box, Typography, Paper, Chip, Divider } from '@mui/material';
// import { FiberManualRecord as FiberManualRecordIcon } from '@mui/icons-material';

// export interface Evidence {
//   id: string;
//   project: string;
//   resource: string;
//   timestamp: string;
//   status: 'Failed to schedule' | 'Normal';
//   details: string;
// }

// interface EvidencePanelProps {
//   evidences: Evidence[];
// }

// const formatDate = (dateString: string) => {
//   const options: Intl.DateTimeFormatOptions = {
//     day: '2-digit',
//     month: 'short',
//     year: 'numeric',
//     hour: 'numeric',
//     minute: '2-digit',
//     hour12: true,
//   };
//   return new Date(dateString).toLocaleString('en-US', options).replace(',', ' •');
// };

// const EvidencePanel: React.FC<EvidencePanelProps> = ({ evidences }) => {
//   return (
//     <Paper
//       elevation={0}
//       sx={{
//         width: '100%',
//         bgcolor: '#FFFFFF',
//         borderRadius: '12px',
//         p: 2.5,
//         height: '100%',
//         overflowY: 'auto',
//         //border: '1px solid #EAECF0'
//       }}
//     >
//       <Typography sx={{ fontFamily:'Inter', fontSize:'16px', fontWeight: 500, color:'#333333', mb: 2 }}>
//         Evidence Panel
//       </Typography>
//       <Divider sx={{ mx: -2.3 }} />
//       {evidences.map((evidence, index) => (
//         <React.Fragment key={evidence.id}>
//           <Box sx={{ py: 2.5 }}>
//             <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
//               <Typography sx={{ fontFamily:'Inter', fontSize:'12px', color:'#666666' }}>
//                 {evidence.project}
//               </Typography>
//               <Typography sx={{ fontFamily:'Inter', fontSize:'12px', color:'#666666' }}>
//                 {formatDate(evidence.timestamp)}
//               </Typography>
//             </Box>
            
//             <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor:'#F9FAFB', p:1, borderRadius: '6px' }}>
//                 <Typography sx={{ fontFamily:'Inter', fontSize:'14px', color:'#333333' }}>
//                     {evidence.resource}
//                 </Typography>
//                 {evidence.status === 'Failed to schedule' && (
//                 <Typography sx={{ fontFamily:'Inter', fontSize:'14px', color:'#CA2B20', display: 'flex', alignItems: 'center' }}>
//                   <Box sx={{ width: '8px', height: '8px', bgcolor: '#CA2B20', borderRadius: '50px', mr: 0.75, flexShrink: 0 }} /> 
//                   {evidence.status}
//                 </Typography>
//                 )

//                 }
//                  {/* {evidence.status === 'Failed to schedule' && (
//                     <Chip
//                         icon={<Box sx={{ width: '8px', height: '8px', bgcolor: '#F04438', borderRadius: '50px', mr: 0.75, flexShrink: 0 }} />}
//                         label={evidence.status}
//                         size="small"
//                         sx={{ bgcolor: '#FEF3F2', color: '#B42318', fontWeight: 500, borderRadius: '16px', p: '2px 8px' }}
//                     />
//                 )} */}
//             </Box>

//             <Typography sx={{ fontFamily:'Inter', fontSize:'14px', color:'#666666', mt: 1 }}>
//               {evidence.details}
//             </Typography>

//           </Box>
//           {index < evidences.length - 1 && <Divider />}
//         </React.Fragment>
//       ))}
//     </Paper>
//   );
// };

// export default EvidencePanel;


// import React from 'react';
// import { Box, Typography, Paper, Chip, Link } from '@mui/material';
// import { ErrorOutline } from '@mui/icons-material';

// export interface Evidence {
//   id: string;
//   project: string;
//   resource: string;
//   timestamp: string;
//   status: 'Failed to schedule' | 'Normal';
//   details: string;
// }

// interface EvidencePanelProps {
//   evidences: Evidence[];
// }

// const formatDate = (dateString: string) => {
//   const options: Intl.DateTimeFormatOptions = {
//     day: '2-digit',
//     month: 'short',
//     year: 'numeric',
//     hour: 'numeric',
//     minute: '2-digit',
//     hour12: true,
//   };
//   return new Date(dateString).toLocaleString('en-US', options).replace(',', ' •');
// };

// const EvidencePanel: React.FC<EvidencePanelProps> = ({ evidences }) => {
//   return (
//     <Paper
//       elevation={0}
//       sx={{
//         width: '100%',
//         bgcolor: '#FFFFFF',
//         borderRadius: '12px',
//         p: 2.5,
//         height: '100%',
//         overflowY: 'auto'
//       }}
//     >
//       <Typography variant="h6" sx={{ mb: 2, fontWeight: 500, fontSize: '1rem' }}>
//         Evidence Panel
//       </Typography>
      
//       {evidences.map((evidence, index) => (
//         <Box key={evidence.id} sx={{ mb: index === evidences.length - 1 ? 0 : 2.5, p: 2, bgcolor: 'white', borderRadius: '8px', border: '1px solid #EAECF0' }}>
//           <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
//             <Typography variant="caption" color="text.secondary">
//               {evidence.project}
//             </Typography>
//             <Typography variant="caption" color="text.secondary">
//               {formatDate(evidence.timestamp)}
//             </Typography>
//           </Box>
//           <Link href="#" underline="always" sx={{ fontWeight: 500, mb: 1, display: 'block', color: 'primary.main' }}>
//             {evidence.resource}
//           </Link>
//           <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
//             <Typography variant="body2" color="text.secondary">
//               {evidence.details}
//             </Typography>
//             {evidence.status === 'Failed to schedule' && (
//               <Chip
//                 icon={<ErrorOutline sx={{ fontSize: '1rem', color: '#B42318 !important' }} />}
//                 label={evidence.status}
//                 size="small"
//                 sx={{ bgcolor: '#FEECEB', color: '#B42318', fontWeight: 500, borderRadius: '6px' }}
//               />
//             )}
//           </Box>
//         </Box>
//       ))}
//     </Paper>
//   );
// };

// export default EvidencePanel;




// import React from 'react';
// import { Box, Typography, Paper, Divider, Chip } from '@mui/material';
// import { ErrorOutline } from '@mui/icons-material';

// export interface Evidence {
//   id: string;
//   project: string;
//   resource: string;
//   timestamp: string;
//   status: 'Failed to schedule' | 'Normal';
//   details: string;
// }

// interface EvidencePanelProps {
//   evidences: Evidence[];
// }

// const formatDate = (dateString: string) => {
//   const options: Intl.DateTimeFormatOptions = {
//     day: '2-digit',
//     month: 'short',
//     year: 'numeric',
//     hour: 'numeric',
//     minute: '2-digit',
//     hour12: true,
//   };
//   return new Date(dateString).toLocaleString('en-US', options).replace(',', ' •');
// };

// const EvidencePanel: React.FC<EvidencePanelProps> = ({ evidences }) => {
//   return (
//     <Paper
//       elevation={0}
//       sx={{
//         width: { xs: '100%', lg: '50%' },
//         height: { xs: 'auto', lg: 'calc(100vh - 250px)' },
//         p: 3,
//         bgcolor: 'white',
//         borderRadius: '12px',
//         border: '1px solid #e0e0e0',
//         overflowY: 'auto',
//       }}
//     >
//       <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
//         Evidence Panel
//       </Typography>
//       <Divider sx={{ mb: 2 }} />
//       {evidences.map((evidence, index) => (
//         <Box key={evidence.id} sx={{ mb: index === evidences.length - 1 ? 0 : 3 }}>
//           <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
//             <Typography variant="caption" color="text.secondary">
//               {evidence.project}
//             </Typography>
//             <Typography variant="caption" color="text.secondary">
//               {formatDate(evidence.timestamp)}
//             </Typography>
//           </Box>
//           <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
//             {evidence.resource}
//           </Typography>
//           <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
//             <Typography variant="body2" color="text.secondary">
//               {evidence.details}
//             </Typography>
//             {evidence.status === 'Failed to schedule' && (
//               <Chip
//                 icon={<ErrorOutline sx={{ fontSize: '1.1rem', color: '#d32f2f !important' }} />}
//                 label={evidence.status}
//                 size="small"
//                 sx={{ bgcolor: 'rgba(211, 47, 47, 0.1)', color: '#d32f2f' }}
//               />
//             )}
//           </Box>
//           {index < evidences.length - 1 && <Divider sx={{ mt: 3 }} />}
//         </Box>
//       ))}
//     </Paper>
//   );
// };

// export default EvidencePanel;
